import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { fetchMorningBriefing, fetchEveningDecompression } from "../api/briefing";
import { useAuth } from "../contexts/AuthContext";
import { useOpeningThinking } from "../hooks/useOpeningThinking";
import { userIsAdmin } from "../lib/adminImport";
import {
  CHAT_GUEST_HOLIDAY_SOFT_CTA_TEXT,
  CHAT_MISS_TEXT,
  CHAT_COMPOSER_PLACEHOLDER,
  CHAT_MAIL_SETUP_ASK_ACTION,
  CHAT_GUEST_KITA_CTA_LABEL,
  ALBIS_ABOUT_LINE,
  ALBIS_CONTACT_EMAIL,
  ALBIS_COPYRIGHT_LINE,
} from "../lib/albisVoice";
import { LOGIN_SCHULE_FROM_CHAT } from "../lib/guestAcquisition";
import {
  ERSTE_HAUSHALTE_CTA_LABEL,
  ERSTE_HAUSHALTE_SCENARIO,
  ersteHaushalteLoginHref,
  isErsteHaushalteScenario,
  parseErsteHaushalteAnswer,
  parseSchoolDemoVariant,
} from "../lib/ersteHaushalte";
import {
  buildGuestWelcomeMessages,
  getChatQuickPrompts,
  getGuestScheduleRecallReply,
  getPromptReply,
  getStubReply,
  isScheduleRecallQuestion,
  isHolidayQuestion,
  shouldShowComposerChips,
  syncAuthChatWelcome,
  WELCOME_MESSAGE_ID,
  SCHOOL_SETUP_PROMPT_ID,
  KIDS_DAYS_PROMPT_ID,
  KIDS_MEMORY_PROMPT_ID,
} from "../lib/chatStub";
import { resolveChatAgendaLists } from "../lib/chatAgenda";
import { createTextReveal } from "../lib/smoothChatText";
import {
  buildScheduleRecallReply,
  fetchCalendarRecallLines,
  fetchScheduleRecallBundle,
} from "../lib/chatScheduleRecall";
import { fetchRegionalCalendarAnswer } from "../api/regionalCalendar";
import {
  confirmSchoolName,
  detectSchoolName,
  fileOtherSchoolName,
  matchSchoolName,
} from "../api/schoolName";
import { fetchCompanionChat } from "../api/companionChat";
import { trackEvent } from "../analytics";
import { ANALYTICS } from "../lib/analyticsEvents";
import {
  PRODUCT_EVENTS,
  logProductEvent,
  mapChatHandledBy,
} from "../lib/productEvents";
import { markInstallEngagement } from "../lib/installPromptStorage";
import { shareMorningBriefingViaWhatsApp } from "../lib/shareMorningBriefing";
import { shareTerminViaWhatsApp } from "../lib/shareTermin";
import { trackSharePromptShown } from "../lib/sharePromptAnalytics";
import {
  getShareableKiTaEvents,
  shareKiTaExtractViaWhatsApp,
  shareKiTaPartnerViaWhatsApp,
  shareKiTaPeerViaWhatsApp,
} from "../lib/shareKiTaWin";
import { fetchFamilyProfile, saveFamilyProfile, saveFamilyProfileChildren } from "../api/familyProfile";
import { fetchHousehold } from "../api/household";
import {
  fetchSchoolEmailAlias,
  fetchSchoolEmails,
  fetchSchoolEmailsUpcoming,
  fetchSchoolTasks,
  patchSchoolEmailEventOverride,
  submitSchoolRemindPrefs,
  rescheduleSchoolDeadlineNudges,
} from "../api/schoolEmails";
import { fetchImprovementIdeas, createImprovementIdea } from "../api/improvementIdeas";
import { fetchScheduleItems, patchScheduleItem, createScheduleItem } from "../api/scheduleItems";
import { interpretScheduleChat } from "../api/scheduleChat";
import { forgetExtraction, purgeExtractionSnapshot, restoreExtraction } from "../api/extractions";
import {
  FORGET_COPY,
  forgetConfirmMessage,
  parseExtractionForgetIntent,
} from "../lib/extractionForget";
import {
  looksLikeScheduleWrite,
  messageFromScheduleTurn,
  SCHEDULE_CHAT_TEXT,
  scheduleFocusFromTurn,
  scheduleSavedText,
  scheduleWriteBody,
  turnAfterScheduleChoice,
} from "../lib/scheduleChat";
import { uploadCaptureImage, fetchImageCapture, fetchOpenImageCaptures, confirmImageCapture, cancelImageCapture } from "../api/captureImage";
import { submitPastedMail } from "../api/mailPaste";
import { looksLikePastedMail, normalizePasteText } from "../lib/pastedMail";
import {
  attachChildChoices,
  buildCaptureConfirmMessage,
  buildCaptureSavedMessage,
  captureAttentionItemsFromSaved,
  captureEventsToPickerItems,
  CAPTURE_CHAT_TEXT,
  pickerItemsToConfirmBody,
} from "../lib/captureChat";
import CaptureConfirmPicker from "../components/CaptureConfirmPicker/CaptureConfirmPicker";
import EventLineFields from "../components/EventLineFields/EventLineFields";
import {
  CALENDAR_PROVIDER_LABEL,
  calendarProviderOrder,
  readCalendarProvider,
  rememberCalendarProvider,
} from "../lib/calendarPreference";
import "../components/CaptureConfirmPicker/CaptureConfirmPicker.css";
import ChatAgendaCard from "../components/ChatAgendaCard/ChatAgendaCard";
import HeuteScheduleSheet from "../components/AnstehendSection/HeuteScheduleSheet";
import useHouseholdAgenda from "../hooks/useHouseholdAgenda";
import {
  buildAutoForwardPrompt,
  buildFirstExtractionCelebrationMessage,
  buildForwardingConfirmationMessage,
  buildKitaRemindPickerDoneMessage,
  buildKitaReminderCardMessage,
  buildKitaShareChatMessage,
  buildSchoolSetupAddressMessage,
  buildShippedIdeaMessage,
  MAIL_SETUP_ASK_ID,
  SCHOOL_SETUP_FLOW_TEXT,
  shouldOfferMailSetupAsk,
} from "../lib/schoolSetupChat";
import {
  SEEN_CONFIRMATION_IDS_KEY,
  SEEN_EXTRACTION_IDS_KEY,
  clearAwaitingForward,
  isAutoForwardPromptShown,
  isAwaitingForward,
  markAutoForwardPromptShown,
  markAwaitingForward,
  planInboundSync,
  readStoredIds,
  safeHttpsUrl,
  writeStoredIds,
  isTerminalExtraction,
} from "../lib/forwardWait";
import {
  dismissShippedIdea,
  getDismissedShippedIdeas,
  isSharePromptShown,
  getSharePromptContext,
} from "../lib/wedgeFunnel";
import { trackKitaReminderPromiseShown, trackKitaReminderTap } from "../lib/kitaReminderAnalytics";
import { trackBriefingPushTap } from "../lib/briefingAnalytics";
import {
  briefingChatMessageFromPayload,
  briefingTextFromSections,
  buildKitaReminderChatMessage,
  isFocusedChatPushDeepLink,
  parseBriefingPushParams,
  parseKitaExtractionBatchParams,
  parseKitaExtractionParams,
  parseKitaReminderParams,
  resolveKitaReminderFromMail,
  resolveKitaRemindersForDate,
} from "../lib/pushDeepLinks";
import {
  applyArrivalLinePatch,
  buildKitaExtractionArrivalMessage,
  buildKitaExtractionBatchArrivalMessage,
  receivedOnZurichDate,
} from "../lib/kitaExtractionArrival";
import { todayYmdZurich } from "../lib/schoolAttention";
import {
  AGE_BAND_OPTIONS,
  PROFILE_FLOW_TEXT,
  ageBandLabel,
  buildProfileDoneText,
  childDraftFromBand,
  shouldOfferDeferredAgeCapture,
} from "../lib/familyProfileChat";
import {
  KITA_DAYS_FLOW_TEXT,
  KITA_WEEKDAY_OPTIONS,
} from "../lib/kitaDaysChat";
import {
  SCHOOL_NO_LABEL,
  SCHOOL_OTHER_LABEL,
  SCHOOL_PICK_CHILD,
  schoolConfirmQuestion,
  schoolNameQuestion,
} from "../lib/schoolNameChat";
import {
  DEFAULT_KINDERGARTEN_WEEKDAYS,
  ageBandFromBirthYm,
  childrenNeedingHortAsk,
  childrenNeedingKitaDays,
  audienceAskChipLabels,
  childrenNeedingAudienceAsk,
  collectAudienceAskLabels,
  childDisplayName,
  childrenNeedNamesToSort,
  isKindergartenChild,
  needsSettingAsk,
  mailboxHasHortEvents,
  mergeAudienceKeepingUnseen,
  newChildId,
  normalizeChildName,
  normalizeChildren,
  parseChildNames,
  unmatchedChildAttentionItems,
  withAudienceOnChild,
  withChildSetting,
  withWeekdaysOnChild,
} from "../lib/familyChildren";
import {
  KIDS_AUDIENCE_TEXT,
  KIDS_DAYS_TEXT,
  KIDS_FLOW_TEXT,
  audienceAskOptions,
  buildKidsRecapText,
  childPickOptions,
  formatChildRecapLine,
  isHortTopicQuestion,
  isKidsMemoryQuestion,
  kidsRecapOptions,
  parseAudienceAnswer,
  parseWhoAnswer,
  settingOptions,
  whoAskOptions,
} from "../lib/familyKidsChat";
import { resolvePostalCode } from "../lib/zurichPostalCodes";
import { inferHolidayJurisdictionFromPostalCode } from "../lib/holidayJurisdiction";
import {
  FEEDBACK_FLOW_TEXT,
  FEEDBACK_PROMPT_ID,
  canShowFeedbackChip,
  getFeedbackQuickPrompt,
  markFeedbackChipCooldown,
  titleFromFeedbackText,
} from "../lib/feedbackChat";
import {
  clearCachedHasExtractions,
  clearWeekAgenda,
  getCachedHasExtractions,
  setCachedHasExtractions,
} from "../lib/chatSessionCache";
import SchoolEmailDemo from "../components/SchoolEmailDemo/SchoolEmailDemo";
import "../pages/SchoolInboxPage.css";
import "../components/AnstehendSection/AnstehendSection.css";

function makeId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const BRIEFING_MESSAGE_ID = "briefing-today";
const HOME_AGENDA_ID = "home-agenda";

/** Follow new bubbles only when the user is already this close to the bottom. */
const CHAT_STICK_BOTTOM_PX = 80;

/** From this local hour on, the chat shows the Evening Decompression instead. */
const EVENING_FROM_HOUR = 17;

function shouldShowChatAction(action, agenda) {
  if (action?.type !== "share_briefing") return true;
  if (agenda.loading) return false;
  return !agenda.householdLinked;
}

function normalizeDaysQueue(queue) {
  return (queue || [])
    .map((entry) => {
      if (entry && typeof entry === "object") {
        return { id: entry.id, kind: entry.kind || "kita" };
      }
      if (entry) return { id: entry, kind: "kita" };
      return null;
    })
    .filter(Boolean);
}

export default function ChatPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const setChatSearchParams = useCallback((next, opts) => {
    if (pathnameRef.current !== "/") return;
    setSearchParams(next, opts);
  }, [setSearchParams]);
  const fromErsteHaushalte = isErsteHaushalteScenario(
    searchParams.get("utm_scenario"),
  );
  const ersteHaushalteDemoVariant = parseSchoolDemoVariant(searchParams.get("demo"));
  const ersteHaushalteJob = parseErsteHaushalteAnswer(searchParams.get("job"));
  const [messages, setMessages] = useState([]);
  const [calendarProvider, setCalendarProvider] = useState(readCalendarProvider);
  const [briefing, setBriefing] = useState(null);
  const [sheetItem, setSheetItem] = useState(null);
  const forgetPurgeRef = useRef(null);
  const scheduleFocusRef = useRef(null);
  const [doneUndoOn, setDoneUndoOn] = useState(null);
  const agenda = useHouseholdAgenda({ enabled: Boolean(user) && !authLoading });
  const refreshAgenda = agenda.refresh;
  const [sharingBriefing, setSharingBriefing] = useState(false);
  const [shareError, setShareError] = useState(null);
  const [draft, setDraft] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [textOverlay, setTextOverlay] = useState({});
  const [briefingDone, setBriefingDone] = useState(false);
  const [setupDone, setSetupDone] = useState(false);
  const [hasExtractions, setHasExtractions] = useState(null);
  const [profileReady, setProfileReady] = useState(null);
  const [feedbackFlowActive, setFeedbackFlowActive] = useState(false);
  const threadRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const inputRef = useRef(null);
  const flowRef = useRef({
    active: false,
    step: null,
    children: [],
    settingIndex: 0,
    daysChildId: null,
    daysKind: "kita",
    daysQueue: [],
    daysAfterMail: false,
    editChildId: null,
    audienceChildId: null,
    audienceQueue: [],
    audienceAfterMail: false,
    audienceLabels: [],
  });
  const schoolSetupStartedRef = useRef(false);
  const askDetectedSchoolRef = useRef(async () => false);
  const inboundWatchGen = useRef(0);
  const inboundShownRef = useRef(new Set());
  const inboundConfirmShownRef = useRef(new Set());
  const pendingNoteRef = useRef(false);
  const autoForwardOfferedRef = useRef(isAutoForwardPromptShown());
  const inboundRef = useRef({});
  const completeProfileFlowRef = useRef(null);
  const startAgeCaptureRef = useRef(() => {});
  const ageCaptureShownRef = useRef(false);
  const followUpBusyRef = useRef(false);
  const followUpsRanRef = useRef(false);
  const runDeferredFollowUpsRef = useRef(async () => {});
  const deferredFollowUpsRef = useRef({
    names: async () => false,
    setting: async () => false,
    age: async () => false,
    afterAnswers: async () => {},
  });
  const kitaDaysAskedRef = useRef(false);
  const kitaDaysFlowPendingRef = useRef(false);
  const hortDaysAskedRef = useRef(false);
  const whoAskedKeysRef = useRef(new Set());
  const skipWhoAskRef = useRef(false);
  const shippedIdeasCheckedRef = useRef(false);
  const schoolLinkHandledRef = useRef(false);
  const kitaReminderHandledRef = useRef(false);
  const kitaExtractionHandledRef = useRef(false);
  const kitaExtractionBatchHandledRef = useRef(false);
  const briefingPushHandledRef = useRef(false);
  const suppressDailyBriefingRef = useRef(isFocusedChatPushDeepLink(searchParams));
  const imageInputRef = useRef(null);
  const capturePollRef = useRef(new Set());
  const captureShownRef = useRef(new Set());
  const pasteCaptureInFlightRef = useRef(false);
  const [remindPickerSaving, setRemindPickerSaving] = useState(false);
  const [captureSavingId, setCaptureSavingId] = useState(null);
  const isAdmin = userIsAdmin(user);
  const hasAssistantMessage = messages.some((m) => m.role === "assistant");
  const showHomeAgenda = Boolean(user) && !agenda.loading && (agenda.items?.length > 0);
  const effectiveHasExtractions = useMemo(() => {
    if (!user) return null;
    if (hasExtractions !== null) return hasExtractions;
    return getCachedHasExtractions();
  }, [user, hasExtractions]);
  const openingCopyPending = Boolean(user) && !hasAssistantMessage && (
    hasExtractions === true || effectiveHasExtractions === true
      ? !briefingDone
      : hasExtractions === false
        ? !setupDone
        : true
  );
  const openingSettled = !user
    ? !authLoading
    : showHomeAgenda || hasAssistantMessage
      ? true
      : hasExtractions === null && effectiveHasExtractions !== true
        ? false
        : effectiveHasExtractions === true || hasExtractions === true
          ? briefingDone
          : hasExtractions === false
            ? setupDone
            : false;
  const showOpeningLine =
    !isThinking &&
    !hasAssistantMessage &&
    (authLoading || !user || (Boolean(user) && ((agenda.loading && !showHomeAgenda) || openingCopyPending)));
  const showWaitLines = showOpeningLine || isThinking;
  const [waitLinesVisible, setWaitLinesVisible] = useState(false);
  useEffect(() => {
    if (!showWaitLines) {
      setWaitLinesVisible(false);
      return undefined;
    }
    const timer = setTimeout(() => setWaitLinesVisible(true), 300);
    return () => clearTimeout(timer);
  }, [showWaitLines]);
  const { startOpeningWork } = useOpeningThinking({
    authLoading: authLoading || (Boolean(user) && !openingSettled && !hasAssistantMessage && !showHomeAgenda),
    hasAssistantMessage: hasAssistantMessage || showHomeAgenda,
  });
  const agendaRecallRef = useRef(agenda);
  const effectiveHasExtractionsRef = useRef(effectiveHasExtractions);
  agendaRecallRef.current = agenda;
  effectiveHasExtractionsRef.current = effectiveHasExtractions;
  const replyRevealRef = useRef(null);
  if (!replyRevealRef.current) {
    replyRevealRef.current = createTextReveal({
      onText(id, text) {
        if (text) setIsThinking(false);
        setTextOverlay((prev) => {
          if (text == null) {
            if (!(id in prev)) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
          }
          if (prev[id] === text) return prev;
          return { ...prev, [id]: text };
        });
      },
    });
  }
  useEffect(() => () => replyRevealRef.current?.stop(), []);

  useEffect(() => {
    if (authLoading) return;
    const welcomeOpts = {
      signedIn: !!user,
      demoVariant: fromErsteHaushalte ? ersteHaushalteDemoVariant : undefined,
      ersteHaushalte: fromErsteHaushalte,
      jobAnswer: fromErsteHaushalte ? ersteHaushalteJob : undefined,
    };
    setMessages((prev) => syncAuthChatWelcome(prev, welcomeOpts));
    if (user) return;
    for (const msg of buildGuestWelcomeMessages(welcomeOpts)) {
      if (msg.text) replyRevealRef.current.start(msg.id, msg.text, { release: true });
    }
  }, [user, authLoading, fromErsteHaushalte, ersteHaushalteDemoVariant, ersteHaushalteJob]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setHasExtractions(null);
      setProfileReady(null);
      clearCachedHasExtractions();
      clearWeekAgenda();
      return;
    }
    let active = true;
    fetchSchoolEmails()
      .then((data) => {
        if (!active) return;
        const hasMail = (data.items || []).length > 0;
        setHasExtractions(hasMail);
        setCachedHasExtractions(hasMail);
      })
      .catch(() => {
        if (active) setHasExtractions(false);
      });
    return () => {
      active = false;
    };
  }, [user, authLoading]);

  const quickPrompts = useMemo(() => {
    const prompts = getChatQuickPrompts({ signedIn: !!user });
    if (user && effectiveHasExtractions && canShowFeedbackChip()) {
      prompts.push(getFeedbackQuickPrompt());
    }
    return prompts;
  }, [user, effectiveHasExtractions]);

  const scrollToBottom = useCallback(() => {
    stickToBottomRef.current = true;
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const handleThreadScroll = useCallback(() => {
    const el = threadRef.current;
    if (!el) return;
    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight <= CHAT_STICK_BOTTOM_PX;
  }, []);

  useLayoutEffect(() => {
    const last = messages[messages.length - 1];
    if (last?.role === "user") stickToBottomRef.current = true;
    if (!stickToBottomRef.current) return;
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isThinking, showHomeAgenda, waitLinesVisible, textOverlay]);

  useEffect(() => {
    if (isFocusedChatPushDeepLink(searchParams)) {
      suppressDailyBriefingRef.current = true;
    }
  }, [searchParams]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) return;
    if (effectiveHasExtractions !== true) return;
    if (isFocusedChatPushDeepLink(searchParams) || suppressDailyBriefingRef.current) {
      suppressDailyBriefingRef.current = true;
      setBriefingDone(true);
      return;
    }
    let active = true;
    // Daily rhythm: Morning Briefing earlier in the day, Evening Decompression
    // from late afternoon on. The sentence sits under the agenda card.
    const isEvening = new Date().getHours() >= EVENING_FROM_HOUR;
    const load = isEvening ? fetchEveningDecompression : fetchMorningBriefing;
    load()
      .then((data) => {
        if (!active || !data) return;
        let text = data.chatText || briefingTextFromSections(data.sections);
        if (!text) return;
        setBriefing(isEvening ? null : data);
        let inserted = false;
        setMessages((prev) => {
          if (prev.some((m) => m.id === BRIEFING_MESSAGE_ID)) return prev;
          inserted = true;
          const briefingMsg = {
            id: BRIEFING_MESSAGE_ID,
            role: "assistant",
            text,
          };
          const [first, ...rest] = prev;
          return first?.id === WELCOME_MESSAGE_ID
            ? [first, briefingMsg, ...rest]
            : [briefingMsg, ...prev];
        });
        if (inserted) replyRevealRef.current.start(BRIEFING_MESSAGE_ID, text, { release: true });
      })
      .catch(() => {})
      .finally(() => {
        if (active) setBriefingDone(true);
      });
    return () => {
      active = false;
    };
  }, [user, authLoading, searchParams, effectiveHasExtractions]);

  useEffect(() => {
    if (authLoading) return;
    if (user) return;
    if (!isFocusedChatPushDeepLink(searchParams)) return;
    const redirect = `${window.location.pathname}${window.location.search}`;
    navigate(`/login?redirect=${encodeURIComponent(redirect)}`, { replace: true });
  }, [authLoading, user, searchParams, navigate]);

  const pushAssistant = useCallback((msg) => {
    const id = msg.id || makeId();
    const text = msg.text != null ? String(msg.text) : "";
    setMessages((prev) => [
      ...prev,
      { ...msg, id, role: "assistant", text },
    ]);
    if (text) replyRevealRef.current.start(id, text, { release: true });
  }, []);

  const rememberId = useCallback((storageKey, memory, id) => {
    const key = String(id || "");
    if (!key || memory.current.has(key)) return false;
    const seen = readStoredIds(storageKey) || [];
    if (seen.includes(key)) {
      memory.current.add(key);
      return false;
    }
    memory.current.add(key);
    writeStoredIds(storageKey, [...seen, key]);
    return true;
  }, []);

  const offerAutoForward = useCallback(async (sender) => {
    if (autoForwardOfferedRef.current || isAutoForwardPromptShown()) {
      autoForwardOfferedRef.current = true;
      return;
    }
    autoForwardOfferedRef.current = true;
    markAutoForwardPromptShown();
    let address = null;
    try {
      const alias = await fetchSchoolEmailAlias();
      address = alias?.address || null;
    } catch {
      address = null;
    }
    const options = address
      ? [{ id: makeId(), label: "Adresse kopieren", value: address, kind: "copy-address" }]
      : undefined;
    pushAssistant({
      text: buildAutoForwardPrompt({ sender: sender || null, address }),
      options,
    });
    inboundRef.current.startWatch?.({ announceTimeout: false });
  }, [pushAssistant]);

  const presentExtractionRow = useCallback(async (row) => {
    if (!row?.id || !rememberId(SEEN_EXTRACTION_IDS_KEY, inboundShownRef, row.id)) {
      return false;
    }
    clearAwaitingForward();
    if ((row.processingStatus || "ready") === "failed") {
      pushAssistant({ text: SCHOOL_SETUP_FLOW_TEXT.mailFailed });
    } else {
      const pdfProcessed = (row.attachmentsMeta || []).some(
        (attachment) => attachment?.kind === "pdf" && attachment?.parsed,
      );
      try {
        await refreshAgenda();
      } catch {
        /* agenda refresh is optional for the arrival bubble */
      }
      pushAssistant(buildKitaExtractionArrivalMessage({
        subject: row.subject,
        events: row.events,
        corrections: row.corrections,
        summary: row.summary,
        extractionId: row.id,
        pdfProcessed,
      }));
    }
    const asked =
      (row.processingStatus || "ready") !== "failed" &&
      (await askDetectedSchoolRef.current({
        text: [row.subject, row.summary, row.originalSender].filter(Boolean).join("\n"),
        sender: row.originalSender || null,
        after: "mail",
      }));
    if (!asked) {
      await offerAutoForward(row.originalSender || null);
      void runDeferredFollowUpsRef.current({ fromMail: true });
    }
    return true;
  }, [offerAutoForward, pushAssistant, refreshAgenda, rememberId]);

  const presentConfirmation = useCallback((confirmation) => {
    if (!confirmation?.id) return false;
    if (!rememberId(SEEN_CONFIRMATION_IDS_KEY, inboundConfirmShownRef, confirmation.id)) {
      return false;
    }
    const url = safeHttpsUrl(confirmation.confirmUrl);
    pushAssistant({
      text: buildForwardingConfirmationMessage({
        provider: confirmation.provider,
        code: confirmation.code,
      }),
      action: url ? { type: "open_url", url } : undefined,
      actionLabel: url ? "Bestätigung öffnen" : undefined,
    });
    return true;
  }, [pushAssistant, rememberId]);

  const applyInboundPlan = useCallback(async (data) => {
    const plan = planInboundSync({
      items: data?.items,
      confirmations: data?.confirmations,
      seenExtractionIds: readStoredIds(SEEN_EXTRACTION_IDS_KEY),
      seenConfirmationIds: readStoredIds(SEEN_CONFIRMATION_IDS_KEY),
    });
    if (plan.seedExtractionIds) {
      writeStoredIds(SEEN_EXTRACTION_IDS_KEY, plan.seedExtractionIds);
      writeStoredIds(SEEN_CONFIRMATION_IDS_KEY, plan.seedConfirmationIds || []);
      plan.seedExtractionIds.forEach((id) => inboundShownRef.current.add(String(id)));
      return { seeded: true, shown: false, pending: false };
    }
    if (plan.confirmation) presentConfirmation(plan.confirmation);
    let mailShown = false;
    if (plan.extraction) {
      mailShown = await presentExtractionRow(plan.extraction);
    } else if (plan.pending && !pendingNoteRef.current) {
      pendingNoteRef.current = true;
      pushAssistant({ text: SCHOOL_SETUP_FLOW_TEXT.attachmentWait });
    }
    return { seeded: false, shown: mailShown, pending: plan.pending };
  }, [presentConfirmation, presentExtractionRow, pushAssistant]);

  const startInboundWatch = useCallback(({ announceTimeout = false } = {}) => {
    const gen = ++inboundWatchGen.current;
    pendingNoteRef.current = false;
    if (announceTimeout) markAwaitingForward();
    const started = Date.now();
    (async () => {
      while (inboundWatchGen.current === gen && Date.now() - started < 12 * 60 * 1000) {
        const elapsed = Date.now() - started;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, elapsed < 90_000 ? 3000 : 12000);
        });
        if (inboundWatchGen.current !== gen) return;
        try {
          // eslint-disable-next-line no-await-in-loop
          const data = await fetchSchoolEmails();
          if (inboundWatchGen.current !== gen) return;
          // eslint-disable-next-line no-await-in-loop
          const result = await applyInboundPlan(data);
          if (result.shown) {
            clearAwaitingForward();
            return;
          }
        } catch {
          /* keep waiting — the parent may still be in the mail app */
        }
      }
      if (inboundWatchGen.current !== gen) return;
      if (announceTimeout) {
        clearAwaitingForward();
        pushAssistant({ text: SCHOOL_SETUP_FLOW_TEXT.stillWaiting });
      }
    })();
  }, [applyInboundPlan, pushAssistant]);

  const beginForwardWatch = useCallback(async () => {
    let alreadyHadMail = false;
    try {
      const data = await fetchSchoolEmails();
      alreadyHadMail = (data.items || []).some(
        (row) => (row.processingStatus || "ready") === "ready",
      );
      const seen = readStoredIds(SEEN_EXTRACTION_IDS_KEY) || [];
      const terminal = (data.items || []).filter(isTerminalExtraction).map((row) => row.id);
      writeStoredIds(SEEN_EXTRACTION_IDS_KEY, [...seen, ...terminal]);
      terminal.forEach((id) => inboundShownRef.current.add(String(id)));
    } catch {
      /* the watch retries the fetch */
    }
    startInboundWatch({ announceTimeout: !alreadyHadMail });
  }, [startInboundWatch]);

  inboundRef.current = {
    startWatch: startInboundWatch,
    offerAutoForward,
    presentExtractionRow,
    apply: applyInboundPlan,
    beginWatch: beginForwardWatch,
  };

  const startSchoolSetupFlow = useCallback(async (opts = {}) => {
    const force = opts.force === true;
    if (!user) return;
    // Explicit ?schoolSetup=1 (e.g. Mehr „Weiterleitung im Chat“) always re-shows steps.
    // A previous "weitergeleitet" tap must not hide the address while no mail has arrived.
    if (!force && schoolSetupStartedRef.current) {
      return;
    }
    schoolSetupStartedRef.current = true;
    try {
      const alias = await fetchSchoolEmailAlias();
      if (!alias?.address) {
        pushAssistant({
          text: "Die Weiterleitungsadresse ist gerade nicht verfügbar — schau unter Mehr bei KiTa & Kindergarten.",
          action: { type: "navigate", path: "/mehr" },
          actionLabel: "Zu Mehr",
        });
        return;
      }
      // Organic offer only (skip forced re-shows from deep links / chips).
      if (!force) {
        logProductEvent(PRODUCT_EVENTS.mail_setup_offered);
      }
      pushAssistant({
        text: buildSchoolSetupAddressMessage(alias.address),
        options: [
          {
            id: makeId(),
            label: "Adresse kopieren",
            value: alias.address,
            kind: "copy-address",
          },
          {
            id: makeId(),
            label: SCHOOL_SETUP_FLOW_TEXT.firstMailChip,
            value: null,
            kind: "school-setup-done",
          },
        ],
      });
      startInboundWatch({ announceTimeout: false });
    } catch {
      pushAssistant({
        text: "Die Weiterleitung konnten wir gerade nicht laden — versuch es unter Mehr nochmal.",
        action: { type: "navigate", path: "/mehr" },
        actionLabel: "Zu Mehr",
      });
    }
  }, [user, pushAssistant, startInboundWatch]);

  const weekdayAskOptions = useCallback((skipLabel, selectedDays = []) => {
    const selected = new Set((selectedDays || []).map(Number));
    return [
      ...KITA_WEEKDAY_OPTIONS.map((o) => ({
        id: makeId(),
        label: o.label,
        value: o.value,
        kind: "kita-day",
        selected: selected.has(o.value),
      })),
      {
        id: makeId(),
        label: "Speichern",
        value: null,
        kind: "kita-days-done",
      },
      {
        id: makeId(),
        label: skipLabel,
        value: null,
        kind: "kita-days-skip",
      },
    ];
  }, []);

  const askDaysForChild = useCallback(
    (child, kind, { afterMail = false, queue = [] } = {}) => {
      const list = normalizeChildren(flowRef.current.children);
      const idx = list.findIndex((c) => c.id === child.id);
      const who = childDisplayName(child, idx >= 0 ? idx : 0);
      flowRef.current = {
        ...flowRef.current,
        daysChildId: child.id,
        daysKind: kind,
        daysQueue: queue,
        daysAfterMail: afterMail,
      };
      if (kind === "hort") {
        pushAssistant({
          text: KIDS_DAYS_TEXT.askHort(who),
          options: weekdayAskOptions("Kein Hort", child.hort_weekdays || []),
        });
        return;
      }
      if (kind === "kiga") {
        const preset = child.kita_weekdays?.length
          ? child.kita_weekdays
          : DEFAULT_KINDERGARTEN_WEEKDAYS;
        pushAssistant({
          text: KIDS_DAYS_TEXT.askKiga(who),
          options: weekdayAskOptions("Passt so", preset),
        });
        return;
      }
      pushAssistant({
        text: list.length > 1 || child.name
          ? KIDS_DAYS_TEXT.askKita(who)
          : KITA_DAYS_FLOW_TEXT.ask,
        options: weekdayAskOptions("Überspringen", child.kita_weekdays || []),
      });
    },
    [pushAssistant, weekdayAskOptions],
  );

  const maybeAskKitaDays = useCallback(async () => {
    if (!user) return false;
    if (kitaDaysFlowPendingRef.current) return true;
    if (kitaDaysAskedRef.current) return false;
    try {
      const profile = await fetchFamilyProfile();
      const needing = childrenNeedingKitaDays(profile?.children);
      if (needing.length === 0) return false;
      const data = await fetchSchoolEmails();
      if (!(data.items || []).length) return false;
      kitaDaysAskedRef.current = true;
      kitaDaysFlowPendingRef.current = true;
      flowRef.current = {
        ...flowRef.current,
        children: normalizeChildren(profile?.children || []),
      };
      const [first, ...rest] = needing;
      askDaysForChild(first, "kita", {
        afterMail: true,
        queue: rest.map((c) => ({ id: c.id, kind: "kita" })),
      });
      return true;
    } catch {
      return false;
    }
  }, [user, askDaysForChild]);

  const maybeAskHortDays = useCallback(async ({ force = false } = {}) => {
    if (!user) return false;
    if (kitaDaysFlowPendingRef.current) return true;
    if (!force && hortDaysAskedRef.current) return false;
    try {
      const profile = await fetchFamilyProfile();
      const needing = childrenNeedingHortAsk(profile?.children);
      if (needing.length === 0) return false;
      const data = await fetchSchoolEmails();
      const hortMail = mailboxHasHortEvents(data.items || []);
      if (!force && !hortMail) return false;
      hortDaysAskedRef.current = true;
      kitaDaysFlowPendingRef.current = true;
      flowRef.current = {
        ...flowRef.current,
        children: normalizeChildren(profile?.children || []),
      };
      const [first, ...rest] = needing;
      askDaysForChild(first, "hort", {
        afterMail: !force,
        queue: rest.map((c) => ({ id: c.id, kind: "hort" })),
      });
      return true;
    } catch {
      return false;
    }
  }, [user, askDaysForChild]);

  const askAudienceForChild = useCallback(
    (child, labels, { afterMail = false, queue = [] } = {}) => {
      const list = normalizeChildren(flowRef.current.children);
      const childNames = list.map((c) => c.name).filter(Boolean);
      const chips = audienceAskChipLabels(labels, childNames);
      if (chips.length === 0) return false;
      const idx = list.findIndex((c) => c.id === child.id);
      const who = childDisplayName(child, idx >= 0 ? idx : 0);
      const preselected = (child.audience || []).filter((label) =>
        chips.some((chip) => String(chip).toLowerCase() === String(label).toLowerCase()),
      );
      flowRef.current = {
        ...flowRef.current,
        active: true,
        step: "audience",
        audienceChildId: child.id,
        audienceQueue: queue,
        audienceAfterMail: afterMail,
        audienceLabels: chips,
      };
      pushAssistant({
        text: KIDS_AUDIENCE_TEXT.ask(who),
        options: audienceAskOptions(chips, preselected, makeId),
      });
      return true;
    },
    [pushAssistant],
  );

  const maybeAskAudience = useCallback(async () => {
    if (!user) return false;
    if (kitaDaysFlowPendingRef.current) return true;
    if (flowRef.current.step === "audience") return true;
    try {
      const [profile, upcoming, mailbox, saved] = await Promise.all([
        fetchFamilyProfile(),
        fetchSchoolEmailsUpcoming({ days: 21 }),
        fetchSchoolEmails(),
        fetchScheduleItems({ days: 21 }).catch(() => ({ items: [] })),
      ]);
      const children = normalizeChildren(profile?.children || []);
      const childNames = children.map((c) => c.name).filter(Boolean);
      const labels = collectAudienceAskLabels(
        [
          ...(upcoming.items || []),
          ...(mailbox.items || []),
          ...captureAttentionItemsFromSaved(saved.items || []),
        ],
        childNames,
      );
      if (labels.length === 0) return false;
      const needing = childrenNeedingAudienceAsk(children, labels);
      if (needing.length === 0) return false;
      kitaDaysFlowPendingRef.current = true;
      flowRef.current = {
        ...flowRef.current,
        children,
      };
      const [first, ...rest] = needing;
      if (
        !askAudienceForChild(first, labels, {
          afterMail: true,
          queue: rest.map((c) => c.id),
        })
      ) {
        kitaDaysFlowPendingRef.current = false;
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }, [user, askAudienceForChild]);

  const askWhoItem = useCallback((item, children) => {
    if (item?.extractionId != null) {
      whoAskedKeysRef.current.add(`${item.extractionId}:${item.eventIndex}`);
    }
    flowRef.current = {
      ...flowRef.current,
      active: true,
      step: "who",
      whoItem: item,
      children,
    };
    pushAssistant({
      text: KIDS_FLOW_TEXT.whoAsk(item.title, item.date),
      options: whoAskOptions(children, makeId),
    });
  }, [pushAssistant]);

  const maybeAskWho = useCallback(async () => {
    if (!user) return false;
    if (kitaDaysFlowPendingRef.current) return true;
    if (flowRef.current.step === "who") return true;
    try {
      const profile = await fetchFamilyProfile();
      const children = normalizeChildren(profile?.children || []);
      if (children.filter((c) => c.name).length < 2) return false;
      const [upcoming, saved] = await Promise.all([
        fetchSchoolEmailsUpcoming({ days: 21 }),
        fetchScheduleItems({ days: 21 }).catch(() => ({ items: [] })),
      ]);
      const unmatched = unmatchedChildAttentionItems(
        [
          ...(upcoming.items || []),
          ...captureAttentionItemsFromSaved(saved.items || []),
        ],
        children,
      ).filter((item) => {
        const key = `${item.extractionId}:${item.eventIndex}`;
        return item.extractionId != null && !whoAskedKeysRef.current.has(key);
      });
      if (unmatched.length === 0) return false;
      flowRef.current.whoQueue = unmatched.slice(1);
      flowRef.current.children = children;
      askWhoItem(unmatched[0], children);
      return true;
    } catch {
      return false;
    }
  }, [user, askWhoItem]);

  const maybeShowFirstExtractionCelebration = useCallback(async () => {
    try {
      if (localStorage.getItem("albis.pendingFirstExtractionChat") !== "1") return false;
      localStorage.removeItem("albis.pendingFirstExtractionChat");

      const [upcomingData, data] = await Promise.all([
        fetchSchoolEmailsUpcoming({ days: 14 }),
        fetchSchoolEmails(),
      ]);
      const remindItemsRaw = (upcomingData.items || []).map((item) => ({
        extractionId: item.extractionId,
        eventIndex: item.eventIndex,
        title: item.title || "Termin",
        date: item.date,
        time: item.time,
        all_day: item.all_day,
        action_type: item.action_type,
        event_kind: item.event_kind,
        source_quote: item.source_quote,
        child: item.child,
        applies_to: item.applies_to,
        remind: !!item.remindEnabled,
      }));
      const remindItems = remindItemsRaw;

      const items = data.items || [];
      if (items.length > 0) {
        setHasExtractions(true);
        setCachedHasExtractions(true);
      }
      const eventCount = remindItems.length || items.reduce(
        (sum, row) => sum + getShareableKiTaEvents(row).length,
        0,
      );
      const hasSummary = items.some((row) => String(row.summary || "").trim());
      trackKitaReminderPromiseShown({
        extraction_count: items.length,
        event_count: eventCount,
      });

      if (remindItems.length > 0) {
        pushAssistant({
          text: buildFirstExtractionCelebrationMessage({ eventCount: remindItems.length }),
          content: "kita-remind-picker",
          remindItems,
        });
        return true;
      }

      pushAssistant({
        text: buildFirstExtractionCelebrationMessage({
          eventCount,
          hasSummary,
        }),
      });
      return false;
    } catch {
      return false;
    }
  }, [pushAssistant]);

  const continueAfterMailCapture = useCallback(async ({ askWho } = {}) => {
    if (askWho === true) skipWhoAskRef.current = false;
    const allowWho = askWho === true || (askWho !== false && !skipWhoAskRef.current);
    kitaDaysFlowPendingRef.current = false;
    if (await maybeAskHortDays()) return;
    if (await maybeAskAudience()) return;
    if (allowWho && (await maybeAskWho())) return;
    const showedPicker = await maybeShowFirstExtractionCelebration();
    if (!showedPicker) await deferredFollowUpsRef.current.age();
  }, [maybeAskHortDays, maybeAskAudience, maybeAskWho, maybeShowFirstExtractionCelebration]);

  const finishSchoolFollowUp = useCallback(async () => {
    const after = flowRef.current.schoolAfter;
    const sender = flowRef.current.schoolSender || null;
    flowRef.current.schoolAfter = null;
    flowRef.current.schoolSender = null;
    flowRef.current.schoolMatches = null;
    flowRef.current.step = null;
    flowRef.current.active = false;
    if (after === "mail" || after === "capture") {
      await offerAutoForward(sender);
      await continueAfterMailCapture({ askWho: true });
    }
  }, [offerAutoForward, continueAfterMailCapture]);

  const showSchoolMatches = useCallback((matches) => {
    const fromLetter = Boolean(flowRef.current.schoolAfter);
    flowRef.current.step = "school-confirm";
    flowRef.current.active = true;
    pushAssistant({
      text: schoolConfirmQuestion(matches),
      options: [
        ...matches.map((match) => ({
          id: makeId(),
          label: match.label,
          value: match.packId,
          kind: "school-pack-yes",
        })),
        {
          id: makeId(),
          label: fromLetter ? SCHOOL_NO_LABEL : SCHOOL_OTHER_LABEL,
          value: flowRef.current.schoolTypedName || null,
          kind: fromLetter ? "school-pack-no" : "school-pack-other",
        },
      ],
    });
  }, [pushAssistant]);

  const askSchoolNameForChild = useCallback((child) => {
    flowRef.current.schoolChildId = child?.id || null;
    flowRef.current.schoolAfter = null;
    flowRef.current.step = "school-name";
    flowRef.current.active = true;
    pushAssistant({
      text: schoolNameQuestion(child ? childDisplayName(child, 0) : ""),
    });
  }, [pushAssistant]);

  const askDetectedSchool = useCallback(async ({ text, sender, after }) => {
    const source = String(text || "").trim();
    if (!source) return false;
    try {
      const profile = await fetchFamilyProfile();
      const children = normalizeChildren(profile?.children);
      const missing = children.filter((child) => !child.school_id);
      if (children.length > 0 && missing.length === 0) return false;
      const result = await detectSchoolName(source);
      const matches = result.matches || [];
      if (!matches.length) return false;
      flowRef.current.schoolAfter = after;
      flowRef.current.schoolSender = sender || null;
      flowRef.current.schoolMatches = matches;
      flowRef.current.schoolTypedName = null;
      flowRef.current.children = missing;
      if (missing.length > 1) {
        flowRef.current.step = "school-pick";
        flowRef.current.active = true;
        pushAssistant({
          text: SCHOOL_PICK_CHILD,
          options: missing.map((child, index) => ({
            id: makeId(),
            label: childDisplayName(child, index),
            value: child.id,
            kind: "school-child",
          })),
        });
        return true;
      }
      flowRef.current.schoolChildId = missing[0]?.id || null;
      showSchoolMatches(matches);
      return true;
    } catch {
      return false;
    }
  }, [pushAssistant, showSchoolMatches]);
  askDetectedSchoolRef.current = askDetectedSchool;

  const beginSchoolNameFromLink = useCallback(async () => {
    logProductEvent(PRODUCT_EVENTS.school_link_opened);
    schoolSetupStartedRef.current = true;
    try {
      const profile = await fetchFamilyProfile();
      const missing = normalizeChildren(profile?.children).filter((child) => !child.school_id);
      flowRef.current.children = missing;
      flowRef.current.schoolMatches = null;
      flowRef.current.schoolAfter = null;
      if (missing.length > 1) {
        flowRef.current.step = "school-pick";
        flowRef.current.active = true;
        pushAssistant({
          text: SCHOOL_PICK_CHILD,
          options: missing.map((child, index) => ({
            id: makeId(),
            label: childDisplayName(child, index),
            value: child.id,
            kind: "school-child",
          })),
        });
        return;
      }
      askSchoolNameForChild(missing[0] || null);
    } catch {
      askSchoolNameForChild(null);
    }
  }, [askSchoolNameForChild, pushAssistant]);

  const refreshChatBriefing = useCallback(async () => {
    const isEvening = new Date().getHours() >= EVENING_FROM_HOUR;
    const load = isEvening ? fetchEveningDecompression : fetchMorningBriefing;
    try {
      const data = await load();
      if (!data) return;
      const text = data.chatText || briefingTextFromSections(data.sections);
      setBriefing(isEvening ? null : data);
      setMessages((prev) => {
        const has = prev.some((m) => m.id === BRIEFING_MESSAGE_ID);
        if (!text) {
          return has ? prev.filter((m) => m.id !== BRIEFING_MESSAGE_ID) : prev;
        }
        if (!has) return prev;
        return prev.map((m) => (m.id === BRIEFING_MESSAGE_ID ? { ...m, text } : m));
      });
    } catch {
      /* optional */
    }
  }, []);

  const applyWhoAnswer = useCallback(
    (answer) => {
      const item = flowRef.current.whoItem;
      const children = normalizeChildren(flowRef.current.children);
      const named = children.filter((c) => c.name);
      let childValue = null;
      if (answer?.kind === "child") {
        childValue = answer.childValue || null;
      } else if (answer?.kind === "both") {
        childValue = named.map((c) => c.name).join(" & ") || null;
      }
      (async () => {
        if (item?.sourceKind === "capture" && item?.savedId) {
          try {
            if (answer?.kind === "none") {
              await patchScheduleItem(item.savedId, { done: true });
            } else if (childValue) {
              await patchScheduleItem(item.savedId, { child: childValue });
            }
          } catch {
            /* optional — still continue */
          }
        } else if (item?.extractionId != null) {
          try {
            if (answer?.kind === "none") {
              await patchSchoolEmailEventOverride(item.extractionId, item.eventIndex, {
                dismiss: true,
              });
            } else if (childValue) {
              await patchSchoolEmailEventOverride(item.extractionId, item.eventIndex, {
                child: childValue,
              });
            }
          } catch {
            /* optional — still continue */
          }
        }
        if (answer?.kind === "none" || answer?.kind === "child" || answer?.kind === "both") {
          await refreshChatBriefing();
        }
        if (answer?.kind === "none") {
          pushAssistant({ text: KIDS_FLOW_TEXT.whoNoneSaved(item?.title) });
        }
        const remaining = flowRef.current.whoQueue || [];
        if (remaining.length > 0) {
          flowRef.current.whoQueue = remaining.slice(1);
          askWhoItem(remaining[0], children);
          return;
        }
        flowRef.current.step = null;
        flowRef.current.whoItem = null;
        flowRef.current.active = false;
        await continueAfterMailCapture({ askWho: true });
      })();
    },
    [askWhoItem, continueAfterMailCapture, pushAssistant, refreshChatBriefing],
  );

  const maybeShowKitaShareChat = useCallback(async () => {
    if (!user || !isSharePromptShown()) return;
    try {
      if (localStorage.getItem("albis.kitaShareChatShown") === "1") return;
      localStorage.setItem("albis.kitaShareChatShown", "1");
    } catch {
      return;
    }
    let hasHousehold = false;
    try {
      const household = await fetchHousehold().catch(() => ({ linked: false }));
      hasHousehold = Boolean(household?.linked);
    } catch {
      hasHousehold = false;
    }
    pushAssistant({
      text: buildKitaShareChatMessage({ hasHousehold }),
      actions: hasHousehold
        ? [{ type: "share_kita_peer", label: "Eltern weiterempfehlen" }]
        : [{ type: "share_kita_partner", label: "Partner einladen" }],
    });
    trackSharePromptShown({
      surface: "chat",
      trigger: getSharePromptContext().trigger || "wedge_win",
    });
  }, [user, pushAssistant]);

  const maybeShowShippedIdeaThanks = useCallback(async () => {
    if (!user || shippedIdeasCheckedRef.current) return;
    shippedIdeasCheckedRef.current = true;
    try {
      const dismissed = new Set(getDismissedShippedIdeas());
      const { ideas } = await fetchImprovementIdeas({ sort: "new" });
      const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
      const recent = (ideas || []).find((idea) => {
        if (!idea.is_mine || !idea.implemented || dismissed.has(idea.id)) return false;
        if (!idea.implemented_at) return true;
        return new Date(idea.implemented_at).getTime() >= weekAgo;
      });
      if (!recent) return;
      dismissShippedIdea(recent.id);
      pushAssistant({
        text: buildShippedIdeaMessage({
          title: recent.title,
          shippedNote: recent.shipped_note,
        }),
        action: { type: "navigate", path: "/verbesserungen" },
        actionLabel: "Mitgestalten",
      });
    } catch {
      /* optional */
    }
  }, [user, pushAssistant]);

  const runDeferredFollowUps = useCallback(async ({ fromMail = false } = {}) => {
    if (!user) return;
    if (followUpsRanRef.current) return;
    if (isFocusedChatPushDeepLink(searchParams) || suppressDailyBriefingRef.current) return;
    if (!fromMail && profileReady !== true && hasExtractions !== true) return;
    if (followUpBusyRef.current) return;
    followUpsRanRef.current = true;
    skipWhoAskRef.current = !fromMail;
    if (flowRef.current.active) {
      maybeShowKitaShareChat();
      maybeShowShippedIdeaThanks();
      return;
    }
    const waiting =
      (await deferredFollowUpsRef.current.names()) ||
      (await deferredFollowUpsRef.current.setting()) ||
      (await maybeAskKitaDays());
    if (!waiting) {
      await continueAfterMailCapture();
    }
    maybeShowKitaShareChat();
    maybeShowShippedIdeaThanks();
  }, [
    user,
    searchParams,
    profileReady,
    hasExtractions,
    maybeShowKitaShareChat,
    maybeShowShippedIdeaThanks,
    maybeAskKitaDays,
    continueAfterMailCapture,
  ]);
  runDeferredFollowUpsRef.current = runDeferredFollowUps;

  useEffect(() => {
    if (!user || authLoading) return;
    if (searchParams.get("schoolSetup") !== "1") return;
    setChatSearchParams({}, { replace: true });
    const settle = startOpeningWork();
    Promise.resolve(startSchoolSetupFlow({ force: true })).finally(settle);
    return settle;
  }, [user, authLoading, searchParams, setChatSearchParams, startSchoolSetupFlow, startOpeningWork]);

  const pushUser = useCallback((text, extra = {}) => {
    trackEvent("chat_message_sent", {
      source: "text",
      ...(extra.prompt_id ? { prompt_id: extra.prompt_id } : {}),
    });
    setMessages((prev) => [...prev, { id: makeId(), role: "user", text }]);
  }, []);

  const consumeOptions = useCallback((messageId) => {
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, options: null } : m)),
    );
  }, []);

  const ageOptions = useCallback(
    (extra = []) => [
      ...AGE_BAND_OPTIONS.map((o) => ({
        id: makeId(),
        label: o.label,
        value: o.value,
        kind: "age",
      })),
      ...extra,
    ],
    [],
  );

  const continueSettingsOrPlz = useCallback(() => {
    const children = normalizeChildren(flowRef.current.children);
    flowRef.current.children = children;
    const idx = children.findIndex((child) => needsSettingAsk(child));
    if (idx >= 0) {
      flowRef.current = { ...flowRef.current, active: true, step: "setting", settingIndex: idx };
      const who = childDisplayName(children[idx], idx);
      pushAssistant({
        text: KIDS_FLOW_TEXT.askSetting(who),
        options: settingOptions(makeId),
      });
      return;
    }
    void (async () => {
      followUpBusyRef.current = true;
      try {
        await completeProfileFlowRef.current?.(null);
        await deferredFollowUpsRef.current.afterAnswers();
      } finally {
        followUpBusyRef.current = false;
      }
    })();
  }, [pushAssistant]);

  const askOnboardingName = useCallback((index) => {
    const children = normalizeChildren(flowRef.current.children);
    let nameIndex = index;
    while (nameIndex < children.length && children[nameIndex]?.name) nameIndex += 1;
    const child = children[nameIndex];
    if (!child) {
      continueSettingsOrPlz();
      return;
    }
    flowRef.current = { ...flowRef.current, active: true, step: "names", nameIndex };
    const band = ageBandFromBirthYm(child.birth_ym);
    const bandBit = ageBandLabel(band);
    const who = bandBit
      ? `${childDisplayName(child, nameIndex)} (${bandBit})`
      : childDisplayName(child, nameIndex);
    pushAssistant({
      text: KIDS_FLOW_TEXT.askNameOne(who),
      options: [
        { id: makeId(), label: KIDS_FLOW_TEXT.namesSkip, value: null, kind: "names-skip" },
      ],
    });
  }, [continueSettingsOrPlz, pushAssistant]);

  const showKidsRecap = useCallback(
    async (profileArg = null) => {
      try {
        const profile = profileArg || (await fetchFamilyProfile());
        const children = normalizeChildren(profile?.children || []);
        flowRef.current = {
          active: true,
          step: "kids-recap",
          children,
          settingIndex: 0,
          daysChildId: null,
          daysKind: "kita",
          daysQueue: [],
          daysAfterMail: false,
          editChildId: null,
        };
        if (children.length === 0) {
          flowRef.current.step = "kids-add-age";
          pushAssistant({
            text: KIDS_FLOW_TEXT.recapEmpty,
            options: ageOptions(),
          });
          return;
        }
        pushAssistant({
          text: buildKidsRecapText(children, {
            quartier: profile?.quartier,
            postalCode: profile?.postalCode,
          }),
          options: kidsRecapOptions(makeId),
        });
      } catch {
        pushAssistant({
          text: "Das Laden hat gerade nicht geklappt — versuch es später nochmal.",
        });
      }
    },
    [pushAssistant, ageOptions],
  );

  const continueDaysQueue = useCallback(
    async (afterMail, queue, children, profile = null) => {
      const remaining = normalizeDaysQueue(queue);
      if (remaining.length > 0) {
        const nxt = children.find((c) => c.id === remaining[0].id);
        if (nxt) {
          askDaysForChild(nxt, remaining[0].kind || "kita", {
            afterMail,
            queue: remaining.slice(1),
          });
          return;
        }
      }
      kitaDaysFlowPendingRef.current = false;
      if (afterMail) {
        await continueAfterMailCapture();
        return;
      }
      await showKidsRecap(
        profile
          ? {
              children,
              quartier: profile.quartier,
              postalCode: profile.postalCode,
            }
          : { children },
      );
    },
    [askDaysForChild, continueAfterMailCapture, showKidsRecap],
  );

  const continueAudienceQueue = useCallback(
    async (afterMail, queue, children) => {
      const remaining = (queue || []).filter(Boolean);
      if (remaining.length > 0) {
        const nxt = children.find((c) => c.id === remaining[0]);
        if (nxt) {
          if (
            askAudienceForChild(nxt, flowRef.current.audienceLabels || [], {
              afterMail,
              queue: remaining.slice(1),
            })
          ) {
            return;
          }
        }
      }
      kitaDaysFlowPendingRef.current = false;
      flowRef.current.step = null;
      if (afterMail) await continueAfterMailCapture();
    },
    [askAudienceForChild, continueAfterMailCapture],
  );

  const persistAudienceAndContinue = useCallback(
    async (nextLabels, { replaceAll = false } = {}) => {
      const { audienceChildId, audienceAfterMail, audienceQueue, audienceLabels } =
        flowRef.current;
      const list = normalizeChildren(flowRef.current.children);
      const child = list.find((c) => c.id === audienceChildId);
      const audience = replaceAll
        ? nextLabels
        : mergeAudienceKeepingUnseen(child?.audience, nextLabels, audienceLabels);
      const next = withAudienceOnChild(list, audienceChildId, audience);
      await saveFamilyProfileChildren(next);
      flowRef.current.children = next;
      const idx = next.findIndex((c) => c.id === audienceChildId);
      const who = childDisplayName(next[idx] || next[0], idx >= 0 ? idx : 0);
      pushAssistant({ text: KIDS_AUDIENCE_TEXT.saved(who, audience) });
      await continueAudienceQueue(audienceAfterMail, audienceQueue, next);
    },
    [continueAudienceQueue, pushAssistant],
  );

  const startDaysEditForChild = useCallback(
    (child, { afterMail = false } = {}) => {
      if (isKindergartenChild(child)) {
        askDaysForChild(child, "kiga", {
          afterMail,
          queue: [{ id: child.id, kind: "hort" }],
        });
        return;
      }
      askDaysForChild(child, "kita", { afterMail });
    },
    [askDaysForChild],
  );

  const openKidsDaysEditor = useCallback(async () => {
    try {
      const profile = await fetchFamilyProfile();
      const children = normalizeChildren(profile?.children || []);
      flowRef.current = {
        ...flowRef.current,
        active: true,
        children,
      };
      if (children.length === 0) {
        await showKidsRecap(profile);
        return;
      }
      if (children.length === 1) {
        startDaysEditForChild(children[0]);
        return;
      }
      flowRef.current.step = "kids-days-pick";
      pushAssistant({
        text: KIDS_FLOW_TEXT.pickChildDays,
        options: childPickOptions(children, makeId, "kids-days-child"),
      });
    } catch {
      pushAssistant({
        text: "Das Laden hat gerade nicht geklappt — versuch es später nochmal.",
      });
    }
  }, [showKidsRecap, startDaysEditForChild, pushAssistant]);

  const completeProfileFlow = useCallback(
    async (plzValue) => {
      const children = normalizeChildren(flowRef.current.children);
      const resolved = plzValue ? resolvePostalCode(plzValue) : null;
      const holiday = inferHolidayJurisdictionFromPostalCode(resolved?.postalCode);
      flowRef.current = {
        active: false,
        step: null,
        children: [],
        settingIndex: 0,
        daysChildId: null,
        daysKind: "kita",
        daysQueue: [],
        daysAfterMail: false,
        editChildId: null,
      };
      try {
        await saveFamilyProfile({
          children,
          postalCode: resolved?.postalCode ?? null,
          quartier: resolved?.quartier ?? null,
          latitude: resolved?.latitude ?? null,
          longitude: resolved?.longitude ?? null,
          holidayJurisdiction: holiday.holidayJurisdiction,
          holidayJurisdictionSource: holiday.holidayJurisdictionSource,
        });
        pushAssistant({
          text: buildProfileDoneText({
            childCount: children.length,
            recapLines: children.map((c, i) => formatChildRecapLine(c, i)),
            quartier: resolved?.quartier ?? null,
            postalCode: resolved?.postalCode ?? null,
          }),
        });
        setProfileReady(true);
      } catch {
        pushAssistant({
          text: "Das Speichern hat gerade nicht geklappt — wir versuchen es später nochmal.",
        });
      }
    },
    [pushAssistant],
  );
  completeProfileFlowRef.current = completeProfileFlow;

  const startAgeCapture = useCallback(() => {
    flowRef.current = {
      active: true,
      step: "age-capture",
      children: [],
      settingIndex: 0,
      daysChildId: null,
      daysKind: "kita",
      daysQueue: [],
      daysAfterMail: false,
      editChildId: null,
    };
    logProductEvent(PRODUCT_EVENTS.onboarding_started);
    pushAssistant({
      text: PROFILE_FLOW_TEXT.askAge,
      content: "age-capture",
      ageChildren: [],
      ageCaptureDone: false,
    });
  }, [pushAssistant]);
  startAgeCaptureRef.current = startAgeCapture;

  const finishAgeCapture = useCallback(
    async (message, { skip }) => {
      if (!message || message.ageCaptureDone || message.ageCaptureSaving) return;
      setMessages((prev) =>
        prev.map((m) => (m.id === message.id ? { ...m, ageCaptureSaving: true } : m)),
      );
      const children = skip
        ? []
        : (message.ageChildren || [])
            .map((row) => childDraftFromBand(row.band))
            .filter(Boolean);
      if (!skip && children.length === 0) {
        setMessages((prev) =>
          prev.map((m) => (m.id === message.id ? { ...m, ageCaptureSaving: false } : m)),
        );
        flowRef.current = { ...flowRef.current, active: true, step: "age-capture" };
        pushAssistant({ text: PROFILE_FLOW_TEXT.ageHint });
        return;
      }
      flowRef.current = { ...flowRef.current, active: false, step: null, children };
      if (children.length === 0) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === message.id
              ? { ...m, ageCaptureDone: true, ageCaptureSaving: false }
              : m,
          ),
        );
        await completeProfileFlow(null);
        return;
      }
      try {
        await saveFamilyProfile({
          children,
          postalCode: null,
          quartier: null,
          latitude: null,
          longitude: null,
          holidayJurisdiction: null,
          holidayJurisdictionSource: null,
        });
        setProfileReady(true);
      } catch {
        setMessages((prev) =>
          prev.map((m) => (m.id === message.id ? { ...m, ageCaptureSaving: false } : m)),
        );
        flowRef.current = { ...flowRef.current, active: true, step: "age-capture" };
        pushAssistant({
          text: "Das Speichern hat gerade nicht geklappt — wir versuchen es später nochmal.",
        });
        return;
      }
      setMessages((prev) =>
        prev.map((m) =>
          m.id === message.id ? { ...m, ageCaptureDone: true, ageCaptureSaving: false } : m,
        ),
      );
      flowRef.current = { ...flowRef.current, active: true, children };
      if (childrenNeedNamesToSort(children)) {
        askOnboardingName(0);
        return;
      }
      continueSettingsOrPlz();
    },
    [completeProfileFlow, pushAssistant, askOnboardingName, continueSettingsOrPlz],
  );

  const householdHasAttention = useCallback(async () => {
    const [mailbox, upcoming, saved] = await Promise.all([
      fetchSchoolEmails(),
      fetchSchoolEmailsUpcoming({ days: 21 }),
      fetchScheduleItems({ days: 21 }).catch(() => ({ items: [] })),
    ]);
    if ((mailbox.items || []).length > 0) return true;
    const items = [
      ...(upcoming.items || []),
      ...captureAttentionItemsFromSaved(saved.items || []),
    ];
    return items.some((item) => {
      const action = String(item?.action_type || item?.actionType || "").toLowerCase();
      if (action === "info") return false;
      return Boolean(item?.date || item?.title);
    });
  }, []);

  const maybeAskNames = useCallback(async () => {
    if (!user) return false;
    if (flowRef.current.step === "names") return true;
    if (flowRef.current.active) return false;
    try {
      const profile = await fetchFamilyProfile();
      const children = normalizeChildren(profile?.children || []);
      if (!childrenNeedNamesToSort(children)) return false;
      if (!(await householdHasAttention())) return false;
      flowRef.current = { ...flowRef.current, active: true, children };
      askOnboardingName(0);
      return true;
    } catch {
      return false;
    }
  }, [user, householdHasAttention, askOnboardingName]);

  const maybeAskSetting = useCallback(async () => {
    if (!user) return false;
    if (flowRef.current.step === "setting") return true;
    if (flowRef.current.active) return false;
    try {
      const profile = await fetchFamilyProfile();
      const children = normalizeChildren(profile?.children || []);
      const idx = children.findIndex((child) => needsSettingAsk(child));
      if (idx < 0) return false;
      if (!(await householdHasAttention())) return false;
      flowRef.current = {
        ...flowRef.current,
        active: true,
        step: "setting",
        settingIndex: idx,
        children,
      };
      pushAssistant({
        text: KIDS_FLOW_TEXT.askSetting(childDisplayName(children[idx], idx)),
        options: settingOptions(makeId),
      });
      return true;
    } catch {
      return false;
    }
  }, [user, householdHasAttention, pushAssistant]);

  const maybeAskAgeCapture = useCallback(async () => {
    if (!user) return false;
    if (ageCaptureShownRef.current || flowRef.current.active) return false;
    try {
      const profile = await fetchFamilyProfile();
      const children = normalizeChildren(profile?.children || []);
      if (
        !shouldOfferDeferredAgeCapture({
          profileReady: !!profile?.completedAt || children.length > 0,
          hasExtractions: true,
        })
      ) {
        return false;
      }
      if (!(await householdHasAttention())) return false;
      ageCaptureShownRef.current = true;
      startAgeCaptureRef.current();
      return true;
    } catch {
      return false;
    }
  }, [user, householdHasAttention]);

  const continueAfterProfileAnswers = useCallback(async () => {
    if (await maybeAskSetting()) return;
    if (await maybeAskKitaDays()) return;
    await continueAfterMailCapture();
  }, [maybeAskSetting, maybeAskKitaDays, continueAfterMailCapture]);

  deferredFollowUpsRef.current = {
    names: maybeAskNames,
    setting: maybeAskSetting,
    age: maybeAskAgeCapture,
    afterAnswers: continueAfterProfileAnswers,
  };

  const handleSelectOption = useCallback(
    (message, option) => {
      if (option.kind === "school-child") {
        consumeOptions(message.id);
        pushUser(option.label);
        const child = normalizeChildren(flowRef.current.children).find(
          (row) => row.id === option.value,
        );
        flowRef.current.schoolChildId = option.value || null;
        if (flowRef.current.schoolMatches?.length) {
          showSchoolMatches(flowRef.current.schoolMatches);
          return;
        }
        askSchoolNameForChild(child || null);
        return;
      }
      if (
        option.kind === "school-pack-yes" ||
        option.kind === "school-pack-no" ||
        option.kind === "school-pack-other"
      ) {
        consumeOptions(message.id);
        pushUser(option.label);
        const childId = flowRef.current.schoolChildId || null;
        const typedName = option.value || flowRef.current.schoolTypedName || "";
        (async () => {
          try {
            if (option.kind === "school-pack-yes") {
              const result = await confirmSchoolName({ packId: option.value, childId });
              pushAssistant({ text: result.datesText });
            } else if (option.kind === "school-pack-other") {
              const result = await fileOtherSchoolName({ name: typedName, childId });
              pushAssistant({ text: result.text });
            }
          } catch (err) {
            pushAssistant({
              text: err instanceof Error ? err.message : "Das Speichern hat gerade nicht geklappt.",
            });
          } finally {
            await finishSchoolFollowUp();
          }
        })();
        return;
      }
      if (
        option.kind === "schedule-cancel" ||
        option.kind === "schedule-pick-date" ||
        option.kind === "schedule-pick-item" ||
        option.kind === "schedule-confirm"
      ) {
        consumeOptions(message.id);
        pushUser(option.label);
        if (option.kind === "schedule-cancel") {
          scheduleFocusRef.current = null;
          pushAssistant({ text: SCHEDULE_CHAT_TEXT.cancelled });
          return;
        }
        if (option.kind === "schedule-pick-date" || option.kind === "schedule-pick-item") {
          const nextTurn = turnAfterScheduleChoice(option);
          const nextFocus = scheduleFocusFromTurn(nextTurn);
          if (nextFocus) scheduleFocusRef.current = nextFocus;
          const next = messageFromScheduleTurn(nextTurn, makeId);
          if (next) pushAssistant(next);
          return;
        }
        const save = option.value || {};
        const body = scheduleWriteBody(save);
        const canSave =
          save.mode === "create" ? Boolean(body.title && body.date) : Boolean(save.itemId && Object.keys(body).length);
        if (!canSave) {
          pushAssistant({ text: SCHEDULE_CHAT_TEXT.saveFailed });
          return;
        }
        setIsThinking(true);
        (async () => {
          try {
            const saved = save.mode === "create"
              ? await createScheduleItem(body)
              : await patchScheduleItem(save.itemId, body);
            const savedTitle = save.draft?.newTitle || save.draft?.title;
            if (savedTitle) {
              scheduleFocusRef.current = {
                itemId: saved?.item?.id || save.itemId || null,
                title: savedTitle,
                draft: save.draft || null,
              };
            }
            pushAssistant({ text: scheduleSavedText(save), showAgenda: true });
            await refreshAgenda();
          } catch {
            pushAssistant({ text: SCHEDULE_CHAT_TEXT.saveFailed });
          } finally {
            setIsThinking(false);
          }
        })();
        return;
      }
      if (option.kind === "keep-extraction" || option.kind === "forget-extraction") {
        consumeOptions(message.id);
        pushUser(option.label);
        if (option.kind === "keep-extraction") {
          pushAssistant({
            text: option.value === "capture" ? FORGET_COPY.keptFlyer : FORGET_COPY.keptMail,
          });
          return;
        }
        const target = option.value || {};
        (async () => {
          try {
            const result = await forgetExtraction({ kind: target.kind, id: target.id });
            const msgId = makeId();
            pushAssistant({
              id: msgId,
              text: target.kind === "capture" ? FORGET_COPY.deletedFlyer : FORGET_COPY.deletedMail,
              action: {
                type: "restore-extraction",
                snapshotId: result.snapshotId,
                kind: target.kind,
              },
              actionLabel: FORGET_COPY.undo,
            });
            refreshAgenda();
            if (forgetPurgeRef.current) window.clearTimeout(forgetPurgeRef.current);
            forgetPurgeRef.current = window.setTimeout(() => {
              if (result.snapshotId) {
                purgeExtractionSnapshot(result.snapshotId).catch(() => {});
              }
              setMessages((prev) =>
                prev.map((m) =>
                  m.id === msgId ? { ...m, action: undefined, actionLabel: undefined } : m,
                ),
              );
            }, 8000);
          } catch (err) {
            pushAssistant({
              text: err instanceof Error ? err.message : "Löschen hat nicht geklappt.",
            });
          }
        })();
        return;
      }
      if (option.kind === "kita-day") {
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== message.id || !Array.isArray(m.options)) return m;
            return {
              ...m,
              options: m.options.map((o) =>
                o.id === option.id ? { ...o, selected: !o.selected } : o,
              ),
            };
          }),
        );
        return;
      }
      if (option.kind === "audience-label") {
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== message.id || !Array.isArray(m.options)) return m;
            return {
              ...m,
              options: m.options.map((o) =>
                o.id === option.id ? { ...o, selected: !o.selected } : o,
              ),
            };
          }),
        );
        return;
      }
      if (option.kind === "kita-days-skip") {
        consumeOptions(message.id);
        pushUser(option.label);
        const { daysKind, daysChildId, daysAfterMail, daysQueue } = flowRef.current;
        const list = normalizeChildren(flowRef.current.children);
        const child = list.find((c) => c.id === daysChildId);
        const who = childDisplayName(child, list.findIndex((c) => c.id === daysChildId));
        (async () => {
          try {
            if (daysKind === "hort" && daysChildId) {
              const next = withWeekdaysOnChild(list, daysChildId, "hort", []);
              await saveFamilyProfileChildren(next);
              flowRef.current.children = next;
              pushAssistant({ text: KIDS_DAYS_TEXT.skippedHort(who) });
              await continueDaysQueue(daysAfterMail, daysQueue, next);
              return;
            }
            if (daysKind === "kiga" && daysChildId) {
              const existing = child?.kita_weekdays?.length
                ? child.kita_weekdays
                : DEFAULT_KINDERGARTEN_WEEKDAYS;
              let next = list;
              if (!(child?.kita_weekdays && child.kita_weekdays.length)) {
                next = withWeekdaysOnChild(
                  list,
                  daysChildId,
                  "kita",
                  DEFAULT_KINDERGARTEN_WEEKDAYS,
                );
                await saveFamilyProfileChildren(next);
                flowRef.current.children = next;
              }
              pushAssistant({ text: KIDS_DAYS_TEXT.savedKiga(who, existing) });
              await continueDaysQueue(daysAfterMail, daysQueue, next);
              return;
            }
            kitaDaysFlowPendingRef.current = false;
            flowRef.current.daysQueue = [];
            pushAssistant({ text: KITA_DAYS_FLOW_TEXT.skipped });
            if (daysAfterMail) await continueAfterMailCapture();
            else await showKidsRecap();
          } catch {
            kitaDaysFlowPendingRef.current = false;
            pushAssistant({
              text: "Speichern hat nicht geklappt — versuch es später nochmal.",
            });
          }
        })();
        return;
      }
      if (option.kind === "kita-days-done") {
        const selectedDays = (message.options || [])
          .filter((o) => o.kind === "kita-day" && o.selected)
          .map((o) => Number(o.value))
          .filter((n) => Number.isInteger(n));
        const { daysKind, daysChildId, daysAfterMail, daysQueue } = flowRef.current;
        if (daysKind !== "hort" && selectedDays.length === 0) {
          pushAssistant({ text: KITA_DAYS_FLOW_TEXT.needOne });
          return;
        }
        consumeOptions(message.id);
        pushUser(option.label);
        (async () => {
          try {
            const profile = await fetchFamilyProfile();
            const fromFlow = normalizeChildren(flowRef.current.children);
            const fromProfile = normalizeChildren(profile?.children || []);
            const base = fromFlow.length ? fromFlow : fromProfile;
            const next =
              daysKind === "hort"
                ? withWeekdaysOnChild(base, daysChildId, "hort", selectedDays)
                : withWeekdaysOnChild(base, daysChildId, "kita", selectedDays);
            await saveFamilyProfileChildren(next);
            flowRef.current.children = next;
            try {
              await rescheduleSchoolDeadlineNudges();
            } catch {
              /* optional */
            }
            const idx = next.findIndex((c) => c.id === daysChildId);
            const who = childDisplayName(next[idx] || next[0], idx >= 0 ? idx : 0);
            if (daysKind === "hort") {
              pushAssistant({ text: KIDS_DAYS_TEXT.savedHort(who, selectedDays) });
            } else if (daysKind === "kiga") {
              pushAssistant({ text: KIDS_DAYS_TEXT.savedKiga(who, selectedDays) });
            } else {
              pushAssistant({
                text: daysChildId
                  ? KIDS_DAYS_TEXT.savedKita(who, selectedDays)
                  : KITA_DAYS_FLOW_TEXT.saved(selectedDays),
              });
            }
            await continueDaysQueue(daysAfterMail, daysQueue, next, profile);
          } catch {
            kitaDaysFlowPendingRef.current = false;
            pushAssistant({
              text: "Speichern hat nicht geklappt — versuch es später nochmal.",
            });
          }
        })();
        return;
      }
      if (
        option.kind === "audience-done" ||
        option.kind === "audience-all" ||
        option.kind === "audience-unknown"
      ) {
        consumeOptions(message.id);
        pushUser(option.label);
        (async () => {
          try {
            if (option.kind === "audience-all" || option.kind === "audience-unknown") {
              await persistAudienceAndContinue([], { replaceAll: true });
              return;
            }
            const selected = (message.options || [])
              .filter((o) => o.kind === "audience-label" && o.selected)
              .map((o) => o.value)
              .filter(Boolean);
            if (selected.length === 0) {
              pushAssistant({
                text: KIDS_AUDIENCE_TEXT.hint,
                options: audienceAskOptions(
                  flowRef.current.audienceLabels || [],
                  [],
                  makeId,
                ),
              });
              return;
            }
            await persistAudienceAndContinue(selected);
          } catch {
            kitaDaysFlowPendingRef.current = false;
            pushAssistant({
              text: "Speichern hat nicht geklappt — versuch es später nochmal.",
            });
          }
        })();
        return;
      }
      consumeOptions(message.id);
      if (option.kind === "age-skip") {
        pushUser(option.label);
        completeProfileFlow(null);
        return;
      }
      if (option.kind === "age" || option.kind === "kids-add-age") {
        const child = childDraftFromBand(option.value);
        const adding = flowRef.current.step === "kids-add-age" || option.kind === "kids-add-age";
        if (child) {
          flowRef.current.children = [...flowRef.current.children, child];
        }
        pushUser(option.label);
        if (adding) {
          const list = normalizeChildren(flowRef.current.children);
          const last = list[list.length - 1];
          if (list.length >= 2) {
            flowRef.current.step = "kids-add-name";
            flowRef.current.editChildId = last?.id || null;
            pushAssistant({
              text: KIDS_FLOW_TEXT.askNameOne("das Kind"),
            });
            return;
          }
          if (last && needsSettingAsk(last)) {
            flowRef.current.step = "kids-add-setting";
            flowRef.current.settingIndex = list.length - 1;
            pushAssistant({
              text: KIDS_FLOW_TEXT.askSetting(childDisplayName(last, list.length - 1)),
              options: settingOptions(makeId),
            });
            return;
          }
          (async () => {
            try {
              await saveFamilyProfileChildren(list);
              pushAssistant({ text: KIDS_FLOW_TEXT.saved });
              await showKidsRecap({ children: list });
            } catch {
              pushAssistant({
                text: "Speichern hat nicht geklappt — versuch es später nochmal.",
              });
            }
          })();
          return;
        }
        pushAssistant({
          text: PROFILE_FLOW_TEXT.askMore,
          options: ageOptions([
            { id: makeId(), label: "Weiter", value: null, kind: "age-done" },
          ]),
        });
        return;
      }
      if (option.kind === "age-done") {
        const children = normalizeChildren(flowRef.current.children);
        flowRef.current.children = children;
        if (children.length >= 2) {
          askOnboardingName(0);
          return;
        }
        continueSettingsOrPlz();
        return;
      }
      if (option.kind === "names-skip") {
        pushUser(option.label);
        continueSettingsOrPlz();
        return;
      }
      if (option.kind === "child-setting") {
        pushUser(option.label);
        const children = normalizeChildren(flowRef.current.children);
        const idx = flowRef.current.settingIndex || 0;
        const childId = children[idx]?.id;
        const next = withChildSetting(children, childId, option.value);
        flowRef.current.children = next;
        if (
          flowRef.current.step === "kids-add-setting" ||
          flowRef.current.step === "kids-setting"
        ) {
          (async () => {
            try {
              await saveFamilyProfileChildren(next);
              pushAssistant({ text: KIDS_FLOW_TEXT.saved });
              await showKidsRecap({ children: next });
            } catch {
              pushAssistant({
                text: "Speichern hat nicht geklappt — versuch es später nochmal.",
              });
            }
          })();
          return;
        }
        continueSettingsOrPlz();
        return;
      }
      if (option.kind === "kids-ok") {
        pushUser(option.label);
        flowRef.current.active = false;
        flowRef.current.step = null;
        pushAssistant({ text: KIDS_FLOW_TEXT.closed });
        return;
      }
      if (option.kind === "kids-days") {
        pushUser(option.label);
        const children = normalizeChildren(flowRef.current.children);
        if (children.length === 0) {
          showKidsRecap();
          return;
        }
        if (children.length === 1) {
          startDaysEditForChild(children[0]);
          return;
        }
        flowRef.current.step = "kids-days-pick";
        pushAssistant({
          text: KIDS_FLOW_TEXT.pickChildDays,
          options: childPickOptions(children, makeId, "kids-days-child"),
        });
        return;
      }
      if (option.kind === "kids-days-child") {
        pushUser(option.label);
        const children = normalizeChildren(flowRef.current.children);
        const child = children.find((c) => c.id === option.value);
        if (!child) {
          showKidsRecap();
          return;
        }
        startDaysEditForChild(child);
        return;
      }
      if (option.kind === "kids-setting") {
        pushUser(option.label);
        const children = normalizeChildren(flowRef.current.children);
        if (children.length === 0) {
          showKidsRecap();
          return;
        }
        if (children.length === 1) {
          flowRef.current.step = "kids-setting";
          flowRef.current.settingIndex = 0;
          pushAssistant({
            text: KIDS_FLOW_TEXT.askSetting(childDisplayName(children[0], 0)),
            options: settingOptions(makeId),
          });
          return;
        }
        flowRef.current.step = "kids-setting-pick";
        pushAssistant({
          text: KIDS_FLOW_TEXT.pickChildSetting,
          options: childPickOptions(children, makeId, "kids-setting-child"),
        });
        return;
      }
      if (option.kind === "kids-setting-child") {
        pushUser(option.label);
        const children = normalizeChildren(flowRef.current.children);
        const idx = children.findIndex((c) => c.id === option.value);
        if (idx < 0) {
          showKidsRecap();
          return;
        }
        flowRef.current.step = "kids-setting";
        flowRef.current.settingIndex = idx;
        pushAssistant({
          text: KIDS_FLOW_TEXT.askSetting(childDisplayName(children[idx], idx)),
          options: settingOptions(makeId),
        });
        return;
      }
      if (option.kind === "kids-name") {
        pushUser(option.label);
        const children = normalizeChildren(flowRef.current.children);
        if (children.length === 1) {
          flowRef.current.step = "kids-name";
          flowRef.current.editChildId = children[0].id;
          pushAssistant({
            text: KIDS_FLOW_TEXT.askNameOne(childDisplayName(children[0], 0)),
          });
          return;
        }
        flowRef.current.step = "kids-name-pick";
        pushAssistant({
          text: KIDS_FLOW_TEXT.pickChildName,
          options: childPickOptions(children, makeId, "kids-name-child"),
        });
        return;
      }
      if (option.kind === "kids-name-child") {
        pushUser(option.label);
        const children = normalizeChildren(flowRef.current.children);
        const child = children.find((c) => c.id === option.value);
        flowRef.current.step = "kids-name";
        flowRef.current.editChildId = child?.id || null;
        pushAssistant({
          text: KIDS_FLOW_TEXT.askNameOne(childDisplayName(child, 0)),
        });
        return;
      }
      if (option.kind === "kids-add") {
        pushUser(option.label);
        flowRef.current.step = "kids-add-age";
        pushAssistant({
          text: KIDS_FLOW_TEXT.addAge,
          options: ageOptions(),
        });
        return;
      }
      if (
        option.kind === "who-child" ||
        option.kind === "who-both" ||
        option.kind === "who-none" ||
        option.kind === "who-unknown"
      ) {
        pushUser(option.label);
        applyWhoAnswer({
          kind:
            option.kind === "who-child"
              ? "child"
              : option.kind === "who-both"
                ? "both"
                : option.kind === "who-none"
                  ? "none"
                  : "unknown",
          childValue: option.kind === "who-child" ? option.value || null : null,
        });
        return;
      }
      if (option.kind === "plz") {
        pushUser(option.label);
        completeProfileFlow(option.value);
        return;
      }
      if (option.kind === "plz-skip") {
        completeProfileFlow(null);
        return;
      }
      if (option.kind === "copy-address") {
        pushUser(option.label);
        (async () => {
          try {
            await navigator.clipboard.writeText(String(option.value || ""));
            trackEvent(ANALYTICS.school_alias_copied, { surface: "chat" });
            pushAssistant({ text: SCHOOL_SETUP_FLOW_TEXT.copied });
            beginForwardWatch();
          } catch {
            pushAssistant({ text: "Kopieren hat nicht geklappt — markiere die Adresse oben manuell." });
          }
        })();
        return;
      }
      if (option.kind === "school-setup-done") {
        pushUser(option.label);
        pushAssistant({ text: SCHOOL_SETUP_FLOW_TEXT.waiting });
        beginForwardWatch();
      }
    },
    [consumeOptions, pushUser, pushAssistant, ageOptions, completeProfileFlow, continueAfterMailCapture, continueDaysQueue, continueSettingsOrPlz, showKidsRecap, startDaysEditForChild, askOnboardingName, applyWhoAnswer, persistAudienceAndContinue, refreshAgenda, setIsThinking, beginForwardWatch, showSchoolMatches, askSchoolNameForChild, finishSchoolFollowUp],
  );

  const handleFlowText = useCallback(
    (text) => {
      setDraft("");
      const step = flowRef.current.step;
      if (step === "school-name") {
        pushUser(text);
        logProductEvent(PRODUCT_EVENTS.school_name_submitted);
        const childId = flowRef.current.schoolChildId || null;
        flowRef.current.schoolTypedName = text;
        (async () => {
          try {
            const result = await matchSchoolName(text);
            const matches = result.matches || [];
            if (!matches.length) {
              const filed = await fileOtherSchoolName({ name: text, childId });
              flowRef.current.step = null;
              flowRef.current.active = false;
              pushAssistant({ text: filed.text });
              return;
            }
            showSchoolMatches(matches);
          } catch (err) {
            pushAssistant({
              text: err instanceof Error ? err.message : "Das hat gerade nicht geklappt.",
            });
          }
        })();
        return;
      }
      if (step === "school-confirm" || step === "school-pick") {
        pushUser(text);
        pushAssistant({ text: "Tippe eine der Optionen an." });
        return;
      }
      if (step === "plz") {
        const resolved = resolvePostalCode(text);
        pushUser(text);
        if (!resolved) {
          pushAssistant({ text: PROFILE_FLOW_TEXT.plzInvalid });
          return;
        }
        completeProfileFlow(resolved.postalCode);
        return;
      }
      if (step === "names") {
        pushUser(text);
        const children = normalizeChildren(flowRef.current.children);
        const idx = flowRef.current.nameIndex || 0;
        const name = normalizeChildName(text) || parseChildNames(text, 1)[0];
        if (!name) {
          pushAssistant({ text: KIDS_FLOW_TEXT.namesHint });
          return;
        }
        if (children[idx]) children[idx] = { ...children[idx], name };
        flowRef.current.children = children;
        const next = idx + 1;
        if (next < children.length) {
          askOnboardingName(next);
          return;
        }
        continueSettingsOrPlz();
        return;
      }
      if (step === "kids-name" || step === "kids-add-name") {
        pushUser(text);
        const name = normalizeChildName(text) || parseChildNames(text, 1)[0];
        if (!name) {
          pushAssistant({ text: KIDS_FLOW_TEXT.namesHint });
          return;
        }
        const children = normalizeChildren(flowRef.current.children).map((c) =>
          c.id === flowRef.current.editChildId ? { ...c, name } : c,
        );
        flowRef.current.children = children;
        (async () => {
          try {
            await saveFamilyProfileChildren(children);
            const last = children.find((c) => c.id === flowRef.current.editChildId);
            if (step === "kids-add-name" && last && needsSettingAsk(last)) {
              flowRef.current.step = "kids-add-setting";
              flowRef.current.settingIndex = children.findIndex((c) => c.id === last.id);
              pushAssistant({
                text: KIDS_FLOW_TEXT.askSetting(name),
                options: settingOptions(makeId),
              });
              return;
            }
            pushAssistant({ text: KIDS_FLOW_TEXT.saved });
            await showKidsRecap({ children });
          } catch {
            pushAssistant({
              text: "Speichern hat nicht geklappt — versuch es später nochmal.",
            });
          }
        })();
        return;
      }
      if (step === "audience") {
        pushUser(text);
        const parsed = parseAudienceAnswer(text, flowRef.current.audienceLabels || []);
        if (!parsed) {
          pushAssistant({
            text: KIDS_AUDIENCE_TEXT.hint,
            options: audienceAskOptions(
              flowRef.current.audienceLabels || [],
              [],
              makeId,
            ),
          });
          return;
        }
        (async () => {
          try {
            if (parsed.kind === "all" || parsed.kind === "unknown") {
              await persistAudienceAndContinue([], { replaceAll: true });
              return;
            }
            await persistAudienceAndContinue(parsed.labels || []);
          } catch {
            kitaDaysFlowPendingRef.current = false;
            pushAssistant({
              text: "Speichern hat nicht geklappt — versuch es später nochmal.",
            });
          }
        })();
        return;
      }
      if (step === "who") {
        pushUser(text);
        const parsed = parseWhoAnswer(text, flowRef.current.children);
        if (!parsed) {
          pushAssistant({
            text: KIDS_FLOW_TEXT.whoHint,
            options: whoAskOptions(flowRef.current.children, makeId),
          });
          return;
        }
        applyWhoAnswer(parsed);
        return;
      }
      if (
        step === "kids-recap" ||
        step === "kids-days-pick" ||
        step === "kids-name-pick" ||
        step === "kids-setting-pick"
      ) {
        pushUser(text);
        pushAssistant({
          text: KIDS_FLOW_TEXT.recapAsk,
          options: kidsRecapOptions(makeId),
        });
        return;
      }
      pushUser(text);
      pushAssistant({ text: PROFILE_FLOW_TEXT.ageHint });
    },
    [pushUser, pushAssistant, completeProfileFlow, continueSettingsOrPlz, showKidsRecap, askOnboardingName, applyWhoAnswer, persistAudienceAndContinue, showSchoolMatches],
  );

  useEffect(() => {
    if (!user) return;
    let active = true;
    fetchFamilyProfile()
      .then((profile) => {
        if (!active) return;
        const hasKids = normalizeChildren(profile?.children || []).length > 0;
        setProfileReady(!!profile?.completedAt || hasKids);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [user]);

  useEffect(() => {
    if (authLoading || !user) return;
    if (searchParams.get("school") !== "1") return;
    if (schoolLinkHandledRef.current) {
      setChatSearchParams({}, { replace: true });
      return;
    }
    schoolLinkHandledRef.current = true;
    schoolSetupStartedRef.current = true;
    setChatSearchParams({}, { replace: true });
    const settle = startOpeningWork();
    Promise.resolve(beginSchoolNameFromLink()).finally(settle);
    return settle;
  }, [user, authLoading, searchParams, setChatSearchParams, beginSchoolNameFromLink, startOpeningWork]);

  useEffect(() => {
    if (authLoading || !user) return;
    if (searchParams.get("school") === "1") return;
    if (searchParams.get("schoolSetup") === "1") return;
    const kidsParam = searchParams.get("kids");
    if (kidsParam === "1" || kidsParam === "days") return;
    if (isFocusedChatPushDeepLink(searchParams)) return;

    if (hasExtractions === true) {
      setSetupDone(true);
      setMessages((prev) =>
        prev.some((m) => m.id === MAIL_SETUP_ASK_ID)
          ? prev.filter((m) => m.id !== MAIL_SETUP_ASK_ID)
          : prev,
      );
      return;
    }

    if (
      !shouldOfferMailSetupAsk({
        hasExtractions,
        setupAlreadyDone: schoolSetupStartedRef.current,
      })
    ) {
      return;
    }

    let active = true;
    Promise.resolve(startSchoolSetupFlow()).finally(() => {
      if (active) setSetupDone(true);
    });
    return () => {
      active = false;
    };
  }, [
    user,
    authLoading,
    hasExtractions,
    searchParams,
    startSchoolSetupFlow,
  ]);

  useEffect(() => {
    if (!user || authLoading) return;
    const kidsParam = searchParams.get("kids");
    if (kidsParam !== "1" && kidsParam !== "days") return;
    setChatSearchParams({}, { replace: true });
    const settle = startOpeningWork();
    (async () => {
      if (kidsParam === "days") {
        await openKidsDaysEditor();
        return;
      }
      await showKidsRecap();
    })().finally(settle);
    return settle;
  }, [user, authLoading, searchParams, setChatSearchParams, showKidsRecap, openKidsDaysEditor, startOpeningWork]);

  const sendMessage = useCallback(
    (rawText, promptId = null) => {
      const text = String(rawText || "").trim();
      if (!text || isThinking) return;

      trackEvent("chat_message_sent", {
        source: "text",
        ...(promptId ? { prompt_id: promptId } : {}),
      });
      const userMsg = { id: makeId(), role: "user", text };
      markInstallEngagement();
      setMessages((prev) => [...prev, userMsg]);
      setDraft("");
      setIsThinking(true);

      const resolveReply = async () => {
        if (user && isHortTopicQuestion(text)) {
          return { kind: "hort-topic" };
        }
        const forgetIntent = user ? parseExtractionForgetIntent(text) : null;
        if (forgetIntent?.kind === "mail") {
          const data = await fetchSchoolEmails();
          const row = (data.items || [])[0];
          if (!row) return { text: FORGET_COPY.noMail };
          const titles = (row.events || [])
            .filter((event) => String(event?.action_type || "").toLowerCase() !== "info")
            .map((event) => event.title)
            .filter(Boolean);
          return forgetConfirmMessage({
            kind: "mail",
            id: row.id,
            subject: row.subject,
            titles,
            makeId,
          });
        }
        if (forgetIntent?.kind === "capture") {
          const saved = await fetchScheduleItems({ days: 365 });
          const captures = (saved.items || []).filter(
            (item) => item.origin === "capture" || item.sourceKind === "capture",
          );
          captures.sort((a, b) => String(b.createdAt || "").localeCompare(String(a.createdAt || "")));
          const latestId = captures[0]?.captureId || captures[0]?.sourceId;
          if (!latestId) return { text: FORGET_COPY.noFlyer };
          const titles = captures
            .filter((item) => (item.captureId || item.sourceId) === latestId)
            .map((item) => item.title)
            .filter(Boolean);
          return forgetConfirmMessage({
            kind: "capture",
            id: latestId,
            titles,
            makeId,
          });
        }
        if (user && isScheduleRecallQuestion(text) && !looksLikeScheduleWrite(text, { focus: scheduleFocusRef.current })) {
          scheduleFocusRef.current = null;
        }
        if (user && looksLikeScheduleWrite(text, { focus: scheduleFocusRef.current })) {
          try {
            const turn = await interpretScheduleChat(text, scheduleFocusRef.current);
            const nextFocus = scheduleFocusFromTurn(turn);
            if (nextFocus) scheduleFocusRef.current = nextFocus;
            const msg = messageFromScheduleTurn(turn, makeId);
            if (msg) return msg;
            return { text: CHAT_MISS_TEXT, companionSource: "schedule_none" };
          } catch {
            /* companion still answers when the draft call fails */
          }
        }
        if (user && isKidsMemoryQuestion(text)) {
          return { kind: "kids-memory" };
        }
        const chipReply = promptId
          ? getPromptReply(promptId, {
              signedIn: !!user,
              demoVariant: fromErsteHaushalte ? ersteHaushalteDemoVariant : undefined,
              utmScenario: fromErsteHaushalte ? ERSTE_HAUSHALTE_SCENARIO : null,
              jobAnswer: fromErsteHaushalte ? ersteHaushalteJob : null,
            })
          : null;
        const wantsScheduleData =
          chipReply?.needsScheduleData ||
          chipReply?.content === "school-demo" ||
          isScheduleRecallQuestion(text) ||
          (chipReply?.needsSchoolData && user);

        if (wantsScheduleData && !user) {
          return getGuestScheduleRecallReply({
            demoVariant: fromErsteHaushalte ? ersteHaushalteDemoVariant : undefined,
            utmScenario: fromErsteHaushalte ? ERSTE_HAUSHALTE_SCENARIO : null,
            jobAnswer: fromErsteHaushalte ? ersteHaushalteJob : null,
          });
        }

        if (wantsScheduleData && user) {
          try {
            const liveAgenda = agendaRecallRef.current;
            const items = await liveAgenda.ensureFull();
            const calendar = await fetchCalendarRecallLines();
            return buildScheduleRecallReply(items, {
              calendarTodayLine: calendar.calendarTodayLine,
              calendarLines: calendar.calendarLines,
              hasExtractions: !!effectiveHasExtractionsRef.current,
              hasScheduleActivity:
                !!effectiveHasExtractionsRef.current ||
                items.length > 0 ||
                calendar.calendarLines.length > 0,
              days: null,
            });
          } catch {
            try {
              const bundle = await fetchScheduleRecallBundle({ days: null });
              return buildScheduleRecallReply(bundle.items, {
                calendarTodayLine: bundle.calendarTodayLine,
                calendarLines: bundle.calendarLines,
                hasExtractions: bundle.hasExtractions,
                hasScheduleActivity: bundle.hasScheduleActivity,
                days: null,
              });
            } catch {
              if (chipReply?.text) return chipReply;
            }
          }
        }
        if (isHolidayQuestion(text)) {
          try {
            const data = await fetchRegionalCalendarAnswer(text);
            const answer = data.answer || "Kalender gerade nicht verfügbar.";
            if (!user) {
              return {
                text: `${answer}\n\n${CHAT_GUEST_HOLIDAY_SOFT_CTA_TEXT}`,
                action: { type: "navigate", path: fromErsteHaushalte ? ersteHaushalteLoginHref({ answer: ersteHaushalteJob }) : LOGIN_SCHULE_FROM_CHAT },
                actionLabel: fromErsteHaushalte ? ERSTE_HAUSHALTE_CTA_LABEL : CHAT_GUEST_KITA_CTA_LABEL,
              };
            }
            return { text: answer };
          } catch {
            const fallback =
              "Schulferien und Feiertage für Stadt Zürich kenne ich — die Abfrage klappt gerade nicht. Versuch es gleich nochmal.";
            if (!user) {
              return {
                text: `${fallback}\n\n${CHAT_GUEST_HOLIDAY_SOFT_CTA_TEXT}`,
                action: { type: "navigate", path: fromErsteHaushalte ? ersteHaushalteLoginHref({ answer: ersteHaushalteJob }) : LOGIN_SCHULE_FROM_CHAT },
                actionLabel: fromErsteHaushalte ? ERSTE_HAUSHALTE_CTA_LABEL : CHAT_GUEST_KITA_CTA_LABEL,
              };
            }
            return { text: fallback };
          }
        }
        if (chipReply?.needsScheduleData || chipReply?.needsSchoolData) {
          return chipReply;
        }
        const stub = chipReply ?? getStubReply(text, { isAdmin });
        if (user && stub?.unmatched) {
          let streamId = null;
          try {
            const data = await fetchCompanionChat(text, {
              onDelta(full) {
                if (!streamId) {
                  streamId = makeId();
                  setMessages((prev) => [
                    ...prev,
                    { id: streamId, role: "assistant", text: full },
                  ]);
                } else {
                  setMessages((prev) =>
                    prev.map((m) => (m.id === streamId ? { ...m, text: full } : m)),
                  );
                }
                replyRevealRef.current.start(streamId, full);
              },
            });
            if (data?.text && streamId) {
              replyRevealRef.current.release(streamId);
              return {
                text: data.text,
                companionSource: data.source || null,
                alreadyShown: true,
              };
            }
            if (data?.text) {
              return {
                text: data.text,
                companionSource: data.source || null,
              };
            }
          } catch {
            if (streamId) {
              setMessages((prev) =>
                prev.map((m) => (m.id === streamId ? { ...m, text: CHAT_MISS_TEXT } : m)),
              );
              replyRevealRef.current.restart(streamId, CHAT_MISS_TEXT);
              return { text: CHAT_MISS_TEXT, companionSource: "llm_error", alreadyShown: true };
            }
          }
        }
        return stub;
      };

      (async () => {
        const reply = await resolveReply();
        logProductEvent(PRODUCT_EVENTS.chat_message_sent, {
          handled_by: mapChatHandledBy(reply),
        });
        const finish = () => {
          setIsThinking(false);
          void runDeferredFollowUpsRef.current();
        };
        if (reply?.alreadyShown) {
          finish();
          return;
        }
        if (reply?.kind === "hort-topic") {
          setIsThinking(false);
          const asked = await maybeAskHortDays({ force: true });
          if (!asked) await showKidsRecap();
          void runDeferredFollowUpsRef.current();
          return;
        }
        if (reply?.kind === "kids-memory") {
          setIsThinking(false);
          await showKidsRecap();
          void runDeferredFollowUpsRef.current();
          return;
        }
        if (reply?.needsSchoolSetup) {
          setIsThinking(false);
          await startSchoolSetupFlow({ force: true });
          void runDeferredFollowUpsRef.current();
          return;
        }
        if (!reply) {
          finish();
          return;
        }
        pushAssistant({
          text: reply.text,
          content: reply.content,
          demoVariant: reply.demoVariant,
          action: reply.action,
          actionLabel: reply.actionLabel,
          actions: reply.actions,
          options: reply.options,
          showAgenda: reply.showAgenda,
          expandAgenda: reply.expandAgenda,
          extendAgenda: reply.extendAgenda,
        });
        finish();
      })();
    },
    [isThinking, isAdmin, user, startSchoolSetupFlow, showKidsRecap, maybeAskHortDays, fromErsteHaushalte, ersteHaushalteDemoVariant, ersteHaushalteJob, pushAssistant],
  );

  const startFeedbackFlow = useCallback(() => {
    setFeedbackFlowActive(true);
    pushAssistant({ text: FEEDBACK_FLOW_TEXT.ask });
  }, [pushAssistant]);

  const handleFeedbackSubmit = useCallback(
    async (rawText) => {
      const text = String(rawText || "").trim();
      if (!text) return;
      setFeedbackFlowActive(false);
      setDraft("");
      pushUser(text);
      setIsThinking(true);
      try {
        await createImprovementIdea({
          title: titleFromFeedbackText(text),
          body: "",
        });
        markFeedbackChipCooldown();
        trackEvent(ANALYTICS.improvement_idea_submitted, { source: "chat" });
        pushAssistant({
          text: FEEDBACK_FLOW_TEXT.success,
          action: { type: "navigate", path: "/verbesserungen" },
          actionLabel: "Mitgestalten",
        });
      } catch {
        pushAssistant({
          text: FEEDBACK_FLOW_TEXT.error,
          action: { type: "navigate", path: "/verbesserungen" },
          actionLabel: "Mitgestalten",
        });
      } finally {
        setIsThinking(false);
      }
    },
    [pushUser, pushAssistant],
  );

  const handleQuickPromptClick = useCallback(
    (item) => {
      if (item.kind === "feedback") {
        trackEvent("chat_message_sent", {
          source: "text",
          prompt_id: FEEDBACK_PROMPT_ID,
        });
        startFeedbackFlow();
        return;
      }
      if (item.kind === "school-setup") {
        trackEvent("chat_message_sent", {
          source: "text",
          prompt_id: SCHOOL_SETUP_PROMPT_ID,
        });
        pushUser(item.message);
        startSchoolSetupFlow({ force: true });
        return;
      }
      if (item.kind === "kids-days") {
        trackEvent("chat_message_sent", {
          source: "text",
          prompt_id: KIDS_DAYS_PROMPT_ID,
        });
        pushUser(item.message);
        openKidsDaysEditor();
        return;
      }
      if (item.kind === "kids-memory") {
        trackEvent("chat_message_sent", {
          source: "text",
          prompt_id: KIDS_MEMORY_PROMPT_ID,
        });
        pushUser(item.message);
        showKidsRecap();
        return;
      }
      sendMessage(item.message, item.id);
    },
    [
      startFeedbackFlow,
      sendMessage,
      pushUser,
      startSchoolSetupFlow,
      openKidsDaysEditor,
      showKidsRecap,
    ],
  );

  useEffect(() => () => {
    inboundWatchGen.current += 1;
  }, []);

  useEffect(() => {
    if (!user || authLoading) return;
    if (isFocusedChatPushDeepLink(searchParams)) return;
    let cancel = false;
    (async () => {
      try {
        const data = await fetchSchoolEmails();
        if (cancel) return;
        const result = await applyInboundPlan(data);
        if (cancel || result?.seeded || result?.shown) return;
        if (isAwaitingForward()) startInboundWatch({ announceTimeout: true });
      } catch {
        /* the next open retries */
      }
    })();
    return () => {
      cancel = true;
    };
  }, [user, authLoading, searchParams, applyInboundPlan, startInboundWatch]);

  useEffect(() => {
    if (!user || authLoading) return;
    const extractionParams = parseKitaExtractionParams(searchParams);
    if (!extractionParams) return;
    if (kitaExtractionHandledRef.current) return;
    kitaExtractionHandledRef.current = true;

    const settle = startOpeningWork();
    (async () => {
      try {
        const mailData = await fetchSchoolEmails();
        const row = (mailData.items || []).find((r) => r.id === extractionParams.extractionId);
        if (!row) {
          setChatSearchParams({}, { replace: true });
          pushAssistant({
            text: "Diese Eltern-Mail finde ich nicht mehr — frag mich, was ansteht.",
          });
          return;
        }
        await presentExtractionRow(row);
        setChatSearchParams({}, { replace: true });
        scrollToBottom();
      } catch {
        pushAssistant({
          text: "Eltern-Mail gerade nicht ladbar — versuch es gleich nochmal.",
        });
      }
    })().finally(settle);
    return settle;
  }, [
    user,
    authLoading,
    searchParams,
    setChatSearchParams,
    pushAssistant,
    scrollToBottom,
    startOpeningWork,
    presentExtractionRow,
  ]);

  useEffect(() => {
    if (!user || authLoading) return;
    const batchParams = parseKitaExtractionBatchParams(searchParams);
    if (!batchParams) return;
    if (kitaExtractionBatchHandledRef.current) return;
    kitaExtractionBatchHandledRef.current = true;

    const settle = startOpeningWork();
    (async () => {
      try {
        const mailData = await fetchSchoolEmails();
        setChatSearchParams({}, { replace: true });
        const targetDate = batchParams.batchDate || todayYmdZurich();
        const count = (mailData.items || []).filter(
          (row) =>
            row.processingStatus === "ready" &&
            receivedOnZurichDate(row.receivedAt, targetDate),
        ).length;
        const arrival = buildKitaExtractionBatchArrivalMessage(count);
        const arrivalMsg = { id: makeId(), role: "assistant", ...arrival };
        setMessages((prev) => [
          ...prev.filter((m) => m.id !== BRIEFING_MESSAGE_ID),
          arrivalMsg,
        ]);
        if (arrivalMsg.text) {
          replyRevealRef.current.start(arrivalMsg.id, arrivalMsg.text, { release: true });
        }
        scrollToBottom();
      } catch {
        pushAssistant({
          text: "Eltern-Mails gerade nicht ladbar — versuch es gleich nochmal.",
        });
      }
    })().finally(settle);
    return settle;
  }, [
    user,
    authLoading,
    searchParams,
    setChatSearchParams,
    pushAssistant,
    scrollToBottom,
    startOpeningWork,
  ]);

  useEffect(() => {
    if (!user || authLoading) return;
    if (searchParams.get("kita_reminder") !== "1") return;
    if (kitaReminderHandledRef.current) return;
    kitaReminderHandledRef.current = true;

    const params = parseKitaReminderParams(searchParams);
    const digest = params?.scope === "tomorrow" && Boolean(params?.reminderDate);
    if (!params?.extractionId && !digest) {
      setChatSearchParams({}, { replace: true });
      return;
    }

    const settle = startOpeningWork();
    (async () => {
      try {
        const [upcomingData, tasksData, mailData] = await Promise.all([
          fetchSchoolEmailsUpcoming({ days: 14 }),
          fetchSchoolTasks({ days: 365 }),
          fetchSchoolEmails(),
        ]);
        const listItems = [...(tasksData.items || []), ...(upcomingData.items || [])];
        const eventCount = listItems.length;
        trackKitaReminderTap({ searchParams, eventCount });
        setChatSearchParams({}, { replace: true });

        if (digest) {
          const cards = resolveKitaRemindersForDate({
            mailRows: mailData.items || [],
            listItems,
            reminderDate: params.reminderDate,
          });
          if (!cards.length) return;
          const reminderMsgs = cards.map((card, index) => ({
            id: makeId(),
            role: "assistant",
            showAgenda: index === 0,
            ...buildKitaReminderChatMessage({
              reminderKind: params.reminderKind,
              ...card,
            }),
          }));
          setMessages((prev) => [
            ...prev.filter((m) => m.id !== BRIEFING_MESSAGE_ID),
            ...reminderMsgs,
          ]);
          for (const msg of reminderMsgs) {
            if (msg.text) replyRevealRef.current.start(msg.id, msg.text, { release: true });
          }
          scrollToBottom();
          return;
        }

        const card = resolveKitaReminderFromMail({
          extractionId: params.extractionId,
          eventIndex: params.eventIndex,
          mailRows: mailData.items || [],
          listItems,
        });
        if (!card) return;

        const reminderMsg = {
          id: makeId(),
          role: "assistant",
          showAgenda: true,
          ...buildKitaReminderChatMessage({
            reminderKind: params.reminderKind,
            ...card,
          }),
        };
        setMessages((prev) => [
          ...prev.filter((m) => m.id !== BRIEFING_MESSAGE_ID),
          reminderMsg,
        ]);
        if (reminderMsg.text) {
          replyRevealRef.current.start(reminderMsg.id, reminderMsg.text, { release: true });
        }
        scrollToBottom();
      } catch {
        /* optional */
      }
    })().finally(settle);
    return settle;
  }, [
    user,
    authLoading,
    searchParams,
    setChatSearchParams,
    scrollToBottom,
    startOpeningWork,
  ]);

  useEffect(() => {
    if (!user || authLoading) return;
    if (searchParams.get("briefing_push") !== "1") return;
    if (briefingPushHandledRef.current) return;
    briefingPushHandledRef.current = true;

    const params = parseBriefingPushParams(searchParams);
    if (!params) {
      setChatSearchParams({}, { replace: true });
      return;
    }

    trackBriefingPushTap({ searchParams });
    setChatSearchParams({}, { replace: true });

    const load =
      params.kind === "evening" ? fetchEveningDecompression : fetchMorningBriefing;

    const settle = startOpeningWork();
    (async () => {
      try {
        const data = await load();
        if (!data) return;
        if (params.kind === "morning") setBriefing(data);
        setMessages((prev) => prev.filter((m) => m.id !== BRIEFING_MESSAGE_ID));
        pushAssistant(
          briefingChatMessageFromPayload(data, {
            kind: params.kind,
            signals: params.signals,
            fromPush: true,
          }),
        );
        scrollToBottom();
      } catch {
        pushAssistant({
          text: "Briefing gerade nicht verfügbar — versuch es gleich nochmal.",
          showAgenda: true,
        });
      }
    })().finally(settle);
    return settle;
  }, [
    user,
    authLoading,
    searchParams,
    setChatSearchParams,
    pushAssistant,
    scrollToBottom,
    startOpeningWork,
  ]);

  useEffect(() => {
    const prompt = searchParams.get("prompt");
    if (!prompt?.trim()) return;
    sendMessage(prompt);
    setChatSearchParams({}, { replace: true });
  }, [searchParams, sendMessage, setChatSearchParams]);

  const toggleRemindPickerItem = useCallback((messageId, itemIndex) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId || !Array.isArray(m.remindItems)) return m;
        const remindItems = m.remindItems.map((item, idx) =>
          idx === itemIndex ? { ...item, remind: !item.remind } : item,
        );
        return { ...m, remindItems };
      }),
    );
  }, []);

  const handleRemindPickerDone = useCallback(
    async (msg) => {
      if (!msg?.remindItems?.length || msg.remindPickerDone) return;
      setRemindPickerSaving(true);
      try {
        await submitSchoolRemindPrefs({
          updates: msg.remindItems.map((item) => ({
            id: item.extractionId,
            eventIndex: item.eventIndex,
            remind: !!item.remind,
          })),
        });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msg.id ? { ...m, remindPickerDone: true, content: null } : m,
          ),
        );
        const enabledCount = msg.remindItems.filter((item) => item.remind).length;
        pushAssistant({
          text: buildKitaRemindPickerDoneMessage(enabledCount),
          showAgenda: true,
          expandAgenda: true,
        });
        refreshAgenda();
        await deferredFollowUpsRef.current.age();
      } catch (err) {
        setShareError(err instanceof Error ? err.message : String(err));
      } finally {
        setRemindPickerSaving(false);
      }
    },
    [pushAssistant, refreshAgenda],
  );

  const decorateCaptureItems = useCallback(async (events) => {
    const items = captureEventsToPickerItems(events);
    try {
      const profile = await fetchFamilyProfile();
      return attachChildChoices(items, profile?.children);
    } catch {
      return items;
    }
  }, []);

  const showCapturePickerMessage = useCallback(
    async (capture) => {
      if (!capture?.id || captureShownRef.current.has(capture.id)) return;
      if (capture.status !== "ready") return;
      captureShownRef.current.add(capture.id);

      const pickerItems = await decorateCaptureItems(capture.events);
      pushAssistant({
        text: buildCaptureConfirmMessage(pickerItems),
        content: "capture-confirm-picker",
        captureId: capture.id,
        captureItems: pickerItems,
        capturePickerDone: false,
      });
    },
    [decorateCaptureItems, pushAssistant],
  );

  const pollCaptureUntilReady = useCallback(
    async (captureId) => {
      if (!captureId || capturePollRef.current.has(captureId)) return;
      capturePollRef.current.add(captureId);

      try {
        for (let attempt = 0; attempt < 45; attempt += 1) {
          // eslint-disable-next-line no-await-in-loop
          await new Promise((r) => {
            setTimeout(r, 2000);
          });
          // eslint-disable-next-line no-await-in-loop
          const data = await fetchImageCapture(captureId);
          const capture = data.capture;
          if (!capture) continue;
          if (capture.status === "ready") {
            showCapturePickerMessage(capture);
            return;
          }
          if (capture.status === "failed") {
            pushAssistant({
              text: capture.errorMessage || CAPTURE_CHAT_TEXT.extractError,
            });
            return;
          }
          if (capture.status === "confirmed" || capture.status === "cancelled") {
            return;
          }
        }
      } catch {
        /* push is fallback */
      } finally {
        capturePollRef.current.delete(captureId);
      }
    },
    [pushAssistant, showCapturePickerMessage],
  );

  const openCaptureById = useCallback(
    async (captureId) => {
      if (!captureId) return;
      try {
        const data = await fetchImageCapture(captureId);
        const capture = data.capture;
        if (!capture) return;
        if (capture.status === "processing") {
          pushAssistant({ text: CAPTURE_CHAT_TEXT.processing });
          pollCaptureUntilReady(captureId);
          return;
        }
        if (capture.status === "failed") {
          pushAssistant({
            text: capture.errorMessage || CAPTURE_CHAT_TEXT.extractError,
          });
          return;
        }
        if (capture.status === "ready") {
          showCapturePickerMessage(capture);
        }
      } catch {
        pushAssistant({ text: CAPTURE_CHAT_TEXT.extractError });
      }
    },
    [pollCaptureUntilReady, pushAssistant, showCapturePickerMessage],
  );

  const startPastedMailCapture = useCallback(
    async (rawText) => {
      const text = normalizePasteText(rawText);
      if (!text || !looksLikePastedMail(text)) return;
      if (isThinking || pasteCaptureInFlightRef.current) return;

      if (!user) {
        navigate(`/login?redirect=${encodeURIComponent("/")}`);
        return;
      }

      trackEvent(ANALYTICS.capture_started, { source: "paste" });
      markInstallEngagement();
      pasteCaptureInFlightRef.current = true;
      setDraft("");
      setIsThinking(true);

      setMessages((prev) => [
        ...prev,
        { id: makeId(), role: "user", text: CAPTURE_CHAT_TEXT.userPastedMail },
      ]);

      try {
        pushAssistant({ text: CAPTURE_CHAT_TEXT.pasteAck });
        const data = await submitPastedMail(text);
        const capture = data.capture;
        if (data.outcome === "picker" && capture?.status === "ready" && data.hasSelectable) {
          captureShownRef.current.add(capture.id);
          const pickerItems = await decorateCaptureItems(capture.events);
          pushAssistant({
            text: buildCaptureConfirmMessage(pickerItems),
            content: "capture-confirm-picker",
            captureId: capture.id,
            captureItems: pickerItems,
            capturePickerDone: false,
            captureSource: "paste",
          });
          return;
        }
        const pasteFallback =
          data.outcome === "duplicate"
            ? CAPTURE_CHAT_TEXT.pasteAlreadySaved
            : data.outcome === "info_only"
              ? CAPTURE_CHAT_TEXT.pasteInfoOnly
              : CAPTURE_CHAT_TEXT.pasteNoDates;
        pushAssistant({
          text: capture?.errorMessage || data.error || pasteFallback,
        });
      } catch (err) {
        pushAssistant({
          text: err instanceof Error ? err.message : CAPTURE_CHAT_TEXT.extractError,
        });
      } finally {
        pasteCaptureInFlightRef.current = false;
        setIsThinking(false);
      }
    },
    [decorateCaptureItems, isThinking, user, navigate, pushAssistant],
  );

  const handleImageFileSelected = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file || isThinking) return;

      if (!user) {
        navigate(`/login?redirect=${encodeURIComponent("/")}`);
        return;
      }

      const allowed = ["image/jpeg", "image/png", "image/webp"];
      if (!allowed.includes(String(file.type || "").toLowerCase())) {
        setShareError("Nur JPEG-, PNG- oder WebP-Bilder werden unterstützt.");
        return;
      }

      trackEvent(ANALYTICS.capture_started, { source: "image" });
      markInstallEngagement();

      setMessages((prev) => [
        ...prev,
        {
          id: makeId(),
          role: "user",
          text: CAPTURE_CHAT_TEXT.userSentPhoto,
        },
      ]);

      try {
        const data = await uploadCaptureImage(file);
        pushAssistant({ text: CAPTURE_CHAT_TEXT.uploadAck });
        if (data.captureId) {
          pollCaptureUntilReady(data.captureId);
        }
      } catch (err) {
        pushAssistant({
          text: err instanceof Error ? err.message : CAPTURE_CHAT_TEXT.extractError,
        });
      }
    },
    [isThinking, user, navigate, pushAssistant, pollCaptureUntilReady],
  );

  const toggleCapturePickerChild = useCallback((messageId, itemIndex, childId) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId || !Array.isArray(m.captureItems)) return m;
        const captureItems = m.captureItems.map((item, idx) => {
          if (idx !== itemIndex || !Array.isArray(item.childChoices)) return item;
          const selected = new Set(item.selectedChildIds || []);
          if (selected.has(childId)) selected.delete(childId);
          else selected.add(childId);
          return { ...item, selectedChildIds: [...selected] };
        });
        return { ...m, captureItems };
      }),
    );
  }, []);

  const editCapturePickerItem = useCallback((messageId, itemIndex, patch) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId || !Array.isArray(m.captureItems)) return m;
        const captureItems = m.captureItems.map((item, idx) => {
          if (idx !== itemIndex) return item;
          const next = { ...item, ...patch };
          if (patch?.all_day === true) {
            next.time = "";
            next.all_day = true;
          } else if (patch?.all_day === false) {
            next.all_day = false;
            next.time = patch.time || "";
          }
          if (patch?.date && !item.selectable && !item.duplicate) {
            next.selectable = true;
            next.selected = true;
          }
          return next;
        });
        return { ...m, captureItems };
      }),
    );
  }, []);

  const saveArrivalLine = useCallback(
    async (messageId, extractionId, eventIndex, patch) => {
      const data = await patchSchoolEmailEventOverride(extractionId, eventIndex, patch);
      const resolved = data?.resolvedEvent || patch;
      setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId || !m.extractionArrival) return m;
          return {
            ...m,
            extractionArrival: applyArrivalLinePatch(m.extractionArrival, eventIndex, resolved),
          };
        }),
      );
      refreshAgenda();
    },
    [refreshAgenda],
  );

  const toggleCapturePickerItem = useCallback((messageId, itemIndex) => {
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId || !Array.isArray(m.captureItems)) return m;
        const captureItems = m.captureItems.map((item, idx) =>
          idx === itemIndex && item.selectable
            ? { ...item, selected: !item.selected }
            : item,
        );
        return { ...m, captureItems };
      }),
    );
  }, []);

  const handleCapturePickerDone = useCallback(
    async (msg) => {
      if (!msg?.captureId || msg.capturePickerDone) return;
      setCaptureSavingId(msg.id);
      setShareError(null);
      const captureSource = msg.captureSource === "paste" ? "paste" : "image";
      trackEvent(ANALYTICS.capture_confirmed, { source: captureSource });
      try {
        const body = pickerItemsToConfirmBody(msg.captureItems);
        const result = await confirmImageCapture(msg.captureId, body);
        const savedItems = (msg.captureItems || []).filter((item) => item.selected);
        const savedCount = Number.isFinite(Number(result.savedCount))
          ? Number(result.savedCount)
          : savedItems.length;
        if (savedCount < 1) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === msg.id
                ? {
                    ...m,
                    capturePickerDone: true,
                    content: null,
                    text: CAPTURE_CHAT_TEXT.pasteAlreadySaved,
                  }
                : m,
            ),
          );
          return;
        }
        trackEvent(ANALYTICS.capture_saved, {
          source: captureSource,
          event_count: savedCount,
        });
        const actions = [];
        if (savedCount > 0) {
          if (savedItems.length === 1) {
            actions.push({
              type: "share_termin_coplan",
              label: "Partner informieren",
              event: {
                title: savedItems[0].title,
                date: savedItems[0].date,
                time: savedItems[0].time,
                all_day: savedItems[0].all_day,
                place: savedItems[0].place,
                source: "capture",
              },
            });
          }
          actions.push({
            type: "share_kita_peer",
            label: "Eltern weiterempfehlen",
            eventCount: savedCount,
            source: "capture",
          });
          actions.push({
            type: "navigate",
            path: "/?schoolSetup=1",
            label: "Weiterleitung einrichten",
          });
        }
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msg.id
              ? {
                  ...m,
                  capturePickerDone: true,
                  content: null,
                  text: buildCaptureSavedMessage(savedCount),
                  actions: savedCount > 0 ? actions : undefined,
                  action: undefined,
                  actionLabel: undefined,
                  showAgenda: true,
                  expandAgenda: true,
                }
              : m,
          ),
        );
        refreshAgenda();
        if (savedCount > 0) {
          let sender = null;
          try {
            const mail = await fetchSchoolEmails();
            sender = (mail.items || []).map((row) => row.originalSender).find(Boolean) || null;
          } catch {
            sender = null;
          }
          const photoText = savedItems.map((item) => item.title).filter(Boolean).join("\n");
          const asked = await askDetectedSchoolRef.current({
            text: photoText,
            sender,
            after: "capture",
          });
          if (!asked) {
            await offerAutoForward(sender);
            await continueAfterMailCapture({ askWho: true });
          }
        }
      } catch (err) {
        setShareError(
          err instanceof Error ? err.message : CAPTURE_CHAT_TEXT.saveError,
        );
      } finally {
        setCaptureSavingId(null);
      }
    },
    [continueAfterMailCapture, refreshAgenda, offerAutoForward],
  );

  const handleCaptureCancel = useCallback((msgId, captureId) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id === msgId
          ? {
              ...m,
              capturePickerDone: true,
              content: null,
              text: CAPTURE_CHAT_TEXT.cancelled,
            }
          : m,
      ),
    );
    if (!captureId) return;
    cancelImageCapture(captureId).catch((err) => {
      setShareError(err instanceof Error ? err.message : String(err));
    });
  }, []);

  useEffect(() => {
    const captureId = searchParams.get("capture");
    if (!captureId?.trim() || !user || authLoading) return;
    setChatSearchParams({}, { replace: true });
    const settle = startOpeningWork();
    Promise.resolve(openCaptureById(captureId.trim())).finally(settle);
    return settle;
  }, [user, authLoading, searchParams, setChatSearchParams, openCaptureById, startOpeningWork]);

  useEffect(() => {
    if (!user || authLoading) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchOpenImageCaptures();
        if (cancelled) return;
        for (const capture of data.captures || []) {
          if (capture.status === "processing") {
            pollCaptureUntilReady(capture.id);
          } else if (capture.status === "ready") {
            showCapturePickerMessage(capture);
          }
        }
      } catch {
        /* next open retries */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, authLoading, pollCaptureUntilReady, showCapturePickerMessage]);

  const handleComposerPaste = useCallback(
    (e) => {
      const text = e.clipboardData?.getData("text/plain") || "";
      if (!looksLikePastedMail(text)) return;
      e.preventDefault();
      startPastedMailCapture(text);
    },
    [startPastedMailCapture],
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    if (looksLikePastedMail(text)) {
      setDraft("");
      startPastedMailCapture(text);
      return;
    }
    if (feedbackFlowActive) {
      handleFeedbackSubmit(text);
      return;
    }
    if (flowRef.current.active) {
      handleFlowText(text);
      return;
    }
    sendMessage(draft);
  };

  const handleAction = async (action) => {
    if (!action) return;
    if (action.type === "open_url") {
      const url = safeHttpsUrl(action.url);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    if (action.type === "navigate" && action.path) {
      if (
        !user &&
        action.path.includes("/login") &&
        (action.path.includes("/schule") ||
          action.path.includes(encodeURIComponent("/schule")))
      ) {
        trackEvent(ANALYTICS.school_guest_cta, {
          source: fromErsteHaushalte ? ERSTE_HAUSHALTE_SCENARIO : "chat",
        });
      }
      navigate(action.path);
      return;
    }
    if (action.type === "start_school_setup") {
      trackEvent("chat_message_sent", {
        source: "text",
        prompt_id: SCHOOL_SETUP_PROMPT_ID,
      });
      setMessages((prev) =>
        prev.map((m) =>
          m.id === MAIL_SETUP_ASK_ID
            ? { ...m, action: undefined, actionLabel: undefined }
            : m,
        ),
      );
      pushUser(CHAT_MAIL_SETUP_ASK_ACTION);
      startSchoolSetupFlow({ force: true });
      return;
    }
    if (action.type === "share_briefing" && briefing) {
      if (!user) {
        navigate(`/login?redirect=${encodeURIComponent("/")}`);
        return;
      }
      setShareError(null);
      setSharingBriefing(true);
      try {
        await shareMorningBriefingViaWhatsApp({ sections: briefing.sections });
      } catch (err) {
        setShareError(err instanceof Error ? err.message : String(err));
      } finally {
        setSharingBriefing(false);
      }
      return;
    }
    if (action.type === "share_kita_extract") {
      try {
        await shareKiTaExtractViaWhatsApp({
          summary: action.summary,
          events: action.events,
          extractionId: action.extractionId,
        });
      } catch (err) {
        setShareError(err instanceof Error ? err.message : String(err));
      }
      return;
    }
    if (action.type === "share_kita_partner") {
      try {
        await shareKiTaPartnerViaWhatsApp();
      } catch (err) {
        setShareError(err instanceof Error ? err.message : String(err));
      }
      return;
    }
    if (action.type === "toggle_kita_remind") {
      const next = !action.remind;
      try {
        await patchSchoolEmailEventOverride(action.extractionId, action.eventIndex, {
          remind: next,
        });
        setMessages((prev) =>
          prev.map((m) => {
            if (
              m.content !== "kita-reminder-card" ||
              m.reminderCard?.extractionId !== action.extractionId ||
              m.reminderCard?.eventIndex !== action.eventIndex
            ) {
              return m;
            }
            return {
              ...m,
              text: buildKitaReminderCardMessage({
                title: m.reminderCard.event?.title,
                date: m.reminderCard.event?.date,
                remindEnabled: next,
                reminderKind: m.reminderCard.reminderKind,
              }),
              reminderCard: { ...m.reminderCard, remindEnabled: next },
            };
          }),
        );
      } catch (err) {
        setShareError(err instanceof Error ? err.message : String(err));
      }
      return;
    }
    if (action.type === "share_kita_peer") {
      try {
        await shareKiTaPeerViaWhatsApp({
          eventCount: action.eventCount,
          source: action.source,
        });
      } catch (err) {
        setShareError(err instanceof Error ? err.message : String(err));
      }
      return;
    }
    if (action.type === "restore-extraction") {
      if (forgetPurgeRef.current) {
        window.clearTimeout(forgetPurgeRef.current);
        forgetPurgeRef.current = null;
      }
      try {
        await restoreExtraction(action.snapshotId);
        refreshAgenda();
        setMessages((prev) =>
          prev.map((m) =>
            m.action?.snapshotId === action.snapshotId
              ? {
                  ...m,
                  text: action.kind === "capture" ? FORGET_COPY.restoredFlyer : FORGET_COPY.restoredMail,
                  action: undefined,
                  actionLabel: undefined,
                }
              : m,
          ),
        );
      } catch (err) {
        setShareError(err instanceof Error ? err.message : String(err));
      }
      return;
    }
    if (action.type === "share_termin_coplan") {
      try {
        await shareTerminViaWhatsApp(action.event);
      } catch (err) {
        setShareError(err instanceof Error ? err.message : String(err));
      }
    }
  };

  const threadMessages = useMemo(() => {
    if (!showHomeAgenda) return messages;
    const card = { id: HOME_AGENDA_ID, role: "assistant", homeAgenda: true };
    return [card, ...messages];
  }, [messages, showHomeAgenda]);

  const messageText = (msg) =>
    Object.prototype.hasOwnProperty.call(textOverlay, msg.id) ? textOverlay[msg.id] : msg.text;

  const showQuickPrompts = shouldShowComposerChips({
    promptCount: quickPrompts.length,
    feedbackFlowActive,
    isThinking,
    guidedFlowActive: !!flowRef.current.active,
    lastMessage: messages[messages.length - 1],
  });

  return (
    <div className="chat-page">
      <div
        className="chat-thread"
        ref={threadRef}
        onScroll={handleThreadScroll}
        aria-live="polite"
        aria-busy={showOpeningLine || isThinking}
      >
        {waitLinesVisible && !isThinking && !showHomeAgenda ? (
          <div className="chat-bubble chat-bubble--assistant chat-bubble--opening" aria-hidden="true">
            <div className="chat-opening-lines">
              <span />
              <span />
            </div>
          </div>
        ) : null}
        {threadMessages.map((msg) => msg.homeAgenda ? (
          <div key={msg.id} className="chat-bubble chat-bubble--assistant chat-home-agenda">
            <ChatAgendaCard
              items={agenda.items}
              loading={agenda.loading}
              error={agenda.error}
              actionError={agenda.actionError}
              undo={doneUndoOn === HOME_AGENDA_ID ? agenda.undo : null}
              onUndo={agenda.undoDone}
              onOpen={setSheetItem}
              onToggleDone={(item) => {
                setDoneUndoOn(HOME_AGENDA_ID);
                agenda.toggleDone(item);
              }}
            />
          </div>
        ) : (
          <div
            key={msg.id}
            className={`chat-bubble chat-bubble--${msg.role}${
              msg.content === "school-demo" ? " chat-bubble--demo" : ""
            }`}
          >
            {messageText(msg) ? <p className="chat-bubble-text">{messageText(msg)}</p> : null}
            {msg.showAgenda && user && (!showHomeAgenda || msg.extendAgenda || msg.expandAgenda) ? (
              <ChatAgendaCard
                {...resolveChatAgendaLists(msg, agenda)}
                loading={agenda.loading}
                error={agenda.error}
                actionError={agenda.actionError}
                undo={msg.id === doneUndoOn ? agenda.undo : null}
                onUndo={agenda.undoDone}
                onOpen={setSheetItem}
                onToggleDone={(item) => {
                  setDoneUndoOn(msg.id);
                  agenda.toggleDone(item);
                }}
              />
            ) : null}
            {msg.content === "school-demo" ? (
              <SchoolEmailDemo compact variant={msg.demoVariant || "deadline"} />
            ) : null}
            {msg.content === "kita-remind-picker" && Array.isArray(msg.remindItems) ? (
              <div className="chat-remind-picker" role="group" aria-label="Erinnerungen wählen">
                <ul className="chat-remind-picker-list">
                  {msg.remindItems.map((item, idx) => (
                    <li key={`${item.extractionId}-${item.eventIndex}`}>
                      <label className="chat-remind-picker-row">
                        <input
                          type="checkbox"
                          checked={!!item.remind}
                          disabled={msg.remindPickerDone || remindPickerSaving}
                          onChange={() => toggleRemindPickerItem(msg.id, idx)}
                        />
                        <span className="chat-remind-picker-label">
                          <span className="chat-remind-picker-title">{item.title}</span>
                          <span className="chat-remind-picker-date">{item.date}</span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
                {!msg.remindPickerDone ? (
                  <button
                    type="button"
                    className="chat-action-btn"
                    disabled={remindPickerSaving}
                    onClick={() => handleRemindPickerDone(msg)}
                  >
                    {remindPickerSaving ? "Speichert…" : "Fertig"}
                  </button>
                ) : null}
              </div>
            ) : null}
            {msg.content === "age-capture" && !msg.ageCaptureDone ? (
              <div className="chat-age-capture" role="group" aria-label="Alter der Kinder">
                <div className="chat-options">
                  {AGE_BAND_OPTIONS.map((band) => (
                    <button
                      key={band.value}
                      type="button"
                      className="chat-option-btn"
                      disabled={msg.ageCaptureSaving || (msg.ageChildren || []).length >= 6}
                      onClick={() => {
                        setMessages((prev) =>
                          prev.map((m) => {
                            if (m.id !== msg.id || m.ageCaptureDone) return m;
                            const ageChildren = [
                              ...(m.ageChildren || []),
                              { id: newChildId(), band: band.value },
                            ];
                            if (ageChildren.length > 6) return m;
                            return { ...m, ageChildren };
                          }),
                        );
                      }}
                    >
                      {band.label}
                    </button>
                  ))}
                </div>
                {(msg.ageChildren || []).length > 0 ? (
                  <ul className="chat-age-capture-list">
                    {msg.ageChildren.map((row, index) => (
                      <li key={row.id}>
                        <span>{`Kind ${index + 1} · ${ageBandLabel(row.band)}`}</span>
                        <button
                          type="button"
                          aria-label={`Kind ${index + 1} entfernen`}
                          disabled={msg.ageCaptureSaving}
                          onClick={() => {
                            setMessages((prev) =>
                              prev.map((m) => {
                                if (m.id !== msg.id || m.ageCaptureDone) return m;
                                return {
                                  ...m,
                                  ageChildren: (m.ageChildren || []).filter((child) => child.id !== row.id),
                                };
                              }),
                            );
                          }}
                        >
                          {PROFILE_FLOW_TEXT.ageRemove}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : null}
                <div className="chat-age-capture-actions">
                  <button
                    type="button"
                    className="chat-action-btn"
                    disabled={msg.ageCaptureSaving}
                    onClick={() => finishAgeCapture(msg, { skip: false })}
                  >
                    {msg.ageCaptureSaving ? "Speichert…" : PROFILE_FLOW_TEXT.ageDone}
                  </button>
                  <button
                    type="button"
                    className="chat-option-btn"
                    disabled={msg.ageCaptureSaving}
                    onClick={() => finishAgeCapture(msg, { skip: true })}
                  >
                    {PROFILE_FLOW_TEXT.ageSkip}
                  </button>
                </div>
              </div>
            ) : null}
            {msg.content === "capture-confirm-picker" &&
            Array.isArray(msg.captureItems) &&
            !msg.capturePickerDone ? (
              <CaptureConfirmPicker
                items={msg.captureItems}
                saving={captureSavingId === msg.id}
                done={!!msg.capturePickerDone}
                onToggle={(idx) => toggleCapturePickerItem(msg.id, idx)}
                onToggleChild={(idx, childId) => toggleCapturePickerChild(msg.id, idx, childId)}
                onEdit={(idx, patch) => editCapturePickerItem(msg.id, idx, patch)}
                onConfirm={() => handleCapturePickerDone(msg)}
                onCancel={() => handleCaptureCancel(msg.id, msg.captureId)}
              />
            ) : null}
            {msg.content === "kita-extraction-arrival" && msg.extractionArrival ? (
              <>
                {Array.isArray(msg.extractionArrival.lines) && msg.extractionArrival.lines.length > 0 ? (
                  <ul className="capture-confirm-picker-list event-arrival-lines">
                    {msg.extractionArrival.lines.map((line) => {
                      const link = (msg.extractionArrival.calendarLinks || []).find(
                        (row) => row.eventIndex === line.eventIndex,
                      );
                      const order = calendarProviderOrder(calendarProvider);
                      return (
                        <li key={line.eventIndex} className="event-arrival-line">
                          <EventLineFields
                            title={line.title}
                            date={line.date}
                            endDate={line.end_date || ""}
                            time={line.time}
                            allDay={line.all_day !== false && !line.time}
                            editable
                            onCommit={(patch) =>
                              saveArrivalLine(
                                msg.id,
                                msg.extractionArrival.extractionId,
                                line.eventIndex,
                                patch,
                              )
                            }
                          />
                          {link ? (
                            <span className="event-arrival-calendars">
                              {order.map((provider) => {
                                const href = provider === "google" ? link.calUrl : link.outlookUrl;
                                if (!href) return null;
                                const lead = calendarProvider === provider;
                                return (
                                  <a
                                    key={provider}
                                    className={`event-arrival-cal${lead ? " event-arrival-cal--lead" : ""}`}
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => {
                                      rememberCalendarProvider(provider);
                                      setCalendarProvider(provider);
                                    }}
                                  >
                                    {CALENDAR_PROVIDER_LABEL[provider]}
                                  </a>
                                );
                              })}
                            </span>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                ) : null}
                {msg.extractionArrival.moreCount > 0 ? (
                  <p className="capture-confirm-picker-hint">
                    {`… und ${msg.extractionArrival.moreCount} weitere.`}
                  </p>
                ) : null}
              <div className="chat-reminder-card-actions">
                {msg.extractionArrival.canShare ? (
                  <button
                    type="button"
                    className="chat-action-btn"
                    onClick={() =>
                      handleAction({
                        type: "share_kita_extract",
                        summary: msg.extractionArrival.summary,
                        events: msg.extractionArrival.shareEvents,
                        extractionId: msg.extractionArrival.extractionId,
                      })
                    }
                  >
                    An Partner senden
                  </button>
                ) : null}
              </div>
              </>
            ) : null}
            {msg.content === "kita-reminder-card" && msg.reminderCard ? (
              <div className="chat-reminder-card-actions">
                <button
                  type="button"
                  className="chat-action-btn"
                  onClick={() =>
                    handleAction({
                      type: "toggle_kita_remind",
                      extractionId: msg.reminderCard.extractionId,
                      eventIndex: msg.reminderCard.eventIndex,
                      remind: msg.reminderCard.remindEnabled,
                    })
                  }
                >
                  {msg.reminderCard.remindEnabled ? "🔔 Erinnerung an" : "🔕 Erinnerung aus"}
                </button>
                {msg.reminderCard.calUrl ? (
                  <a
                    className="chat-action-btn"
                    href={msg.reminderCard.calUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Zu Google Kalender
                  </a>
                ) : null}
                {msg.reminderCard.outlookUrl ? (
                  <a
                    className="chat-action-btn"
                    href={msg.reminderCard.outlookUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Zu Outlook
                  </a>
                ) : null}
              </div>
            ) : null}
            {Array.isArray(msg.actions) && msg.actions.length
              ? msg.actions.map((act) => (
                  <button
                    key={act.type}
                    type="button"
                    className="chat-action-btn"
                    onClick={() => handleAction(act)}
                  >
                    {act.label}
                  </button>
                ))
              : null}
            {msg.action && msg.actionLabel && shouldShowChatAction(msg.action, agenda) ? (
              <button
                type="button"
                className="chat-action-btn"
                onClick={() => handleAction(msg.action)}
                disabled={msg.action.type === "share_briefing" && sharingBriefing}
              >
                {msg.action.type === "share_briefing" && sharingBriefing
                  ? "Link wird erstellt…"
                  : msg.actionLabel}
              </button>
            ) : null}
            {Array.isArray(msg.options) && msg.options.length ? (
              <div className="chat-options" role="group">
                {msg.options.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`chat-option-btn${opt.selected ? " chat-option-btn--selected" : ""}`}
                    aria-pressed={
                      opt.kind === "kita-day" || opt.kind === "audience-label"
                        ? !!opt.selected
                        : undefined
                    }
                    onClick={() => handleSelectOption(msg, opt)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ))}
        {waitLinesVisible && !isThinking && showHomeAgenda ? (
          <div className="chat-bubble chat-bubble--assistant chat-bubble--opening" aria-hidden="true">
            <div className="chat-opening-lines">
              <span />
              <span />
            </div>
          </div>
        ) : null}
        {waitLinesVisible && isThinking ? (
          <div className="chat-bubble chat-bubble--assistant chat-bubble--opening" aria-hidden="true">
            <div className="chat-opening-lines">
              <span />
              <span />
            </div>
          </div>
        ) : null}
        {shareError ? (
          <p className="chat-share-error" role="alert">
            {shareError}
          </p>
        ) : null}
      </div>

      <div className="chat-footer">
        {showQuickPrompts ? (
          <div className="chat-quick-prompts" role="group" aria-label="Schnellfragen">
            {quickPrompts.map((item) => (
              <button
                key={item.id}
                type="button"
                className="chat-quick-prompt-btn"
                onClick={() => handleQuickPromptClick(item)}
                disabled={isThinking}
              >
                {item.label}
              </button>
            ))}
          </div>
        ) : null}

        <form className="chat-composer" onSubmit={handleSubmit}>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="chat-attach-input"
            aria-hidden="true"
            tabIndex={-1}
            onChange={handleImageFileSelected}
          />
          <button
            type="button"
            className="chat-attach-btn"
            aria-label="Foto senden"
            title="Flyer oder Screenshot senden"
            disabled={isThinking || !user}
            onClick={() => {
              if (!user) {
                navigate(`/login?redirect=${encodeURIComponent("/")}`);
                return;
              }
              imageInputRef.current?.click();
            }}
          >
            📷
          </button>
          <input
            ref={inputRef}
            type="text"
            className="chat-composer-input"
            placeholder={feedbackFlowActive ? "Dein Vorschlag…" : CHAT_COMPOSER_PLACEHOLDER}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onPaste={handleComposerPaste}
            aria-label="Nachricht an Albis"
            autoComplete="off"
          />
          <button
            type="button"
            className="chat-voice-btn"
            aria-label="Spracheingabe (demnächst)"
            title="Spracheingabe — demnächst verfügbar"
            disabled
          >
            🎤
          </button>
          <button
            type="submit"
            className="chat-send-btn"
            aria-label="Senden"
            disabled={!draft.trim() || isThinking}
          >
            ↑
          </button>
        </form>

        {user ? null : (
          <p className="chat-footer-hint">
            {ALBIS_ABOUT_LINE} · {ALBIS_COPYRIGHT_LINE} ·{" "}
            <a href={`mailto:${ALBIS_CONTACT_EMAIL}`} className="chat-footer-link">
              {ALBIS_CONTACT_EMAIL}
            </a>
          </p>
        )}
      </div>
      {user ? (
        <HeuteScheduleSheet
          item={sheetItem}
          open={!!sheetItem}
          householdLinked={agenda.householdLinked}
          onClose={() => setSheetItem(null)}
          onRefresh={agenda.refresh}
          onToggleRemind={agenda.toggleRemind}
          onIrrelevant={agenda.markIrrelevant}
          analyticsSource="chat"
        />
      ) : null}
    </div>
  );
}
